import {
  AppletInstance,
  ConsentStatusEnum,
  ProductName,
} from '@pypestream/api-services';
import {
  Account,
  AddUserToAccountMutation,
  ConnectAuthMethodMutation,
  CreateAccountMutation,
  CreateAppletInstanceMutation,
  CreateTeamMutation,
  GetChildAccountsQuery,
  GetNewTeamDetailsQuery,
  GetTeamDetailsQuery,
  GetTeamProjectsDetailsQuery,
  QueryMetadata,
  SetTeamAccountProjectRolesMutationVariables,
  SetUserAccountProjectsRolesMutationVariables,
  UpdateAccountMutation,
  UpdateAppletInstanceMutation,
  UpdateTeamMutation,
  urqlGql,
  UserStatus,
  GetProjectDetailsQuery,
  GetAppletTemplatesQuery,
  UpdateProjectMutation,
} from '@pypestream/api-services/urql';
import { changeLanguage } from '@pypestream/translations';
import {
  sort,
  transformProfilePicture,
  transformTimezonesData,
} from '@pypestream/utils';
import { assign as immerAssign } from '@xstate/immer';

import { reportManagerFailure } from './report-manager-failure';
import {
  compact,
  flatten,
  isEqual,
  omit,
  orderBy,
  sortBy,
  uniq,
  uniqBy,
} from 'lodash';
import { TreeUtils } from 'simple-tree-utils';
import { assign, Machine, raise, Sender } from 'xstate';

import { getProducts, haveErrors, initialTools, Product } from '../utils';
import { NotFoundError } from '../utils/errors';
import {
  globalAppService,
  sendManagerEvent,
  sendUserEvent,
} from './app.xstate';
import {
  GLOBAL_APP_SUB_MACHINE_IDS,
  sendUserMessage,
} from './app.xstate-utils';
import {
  ManagerContext,
  ManagerEvents,
  ManagerState,
  ManagerTypestates,
  Org,
  OrgWithChildren,
  Project,
  ProjectNew,
  Timezone,
} from './manager.xstate.types';
import {
  handleAddProjectAccess,
  handleUpdateUserDetails,
  hasPendingUpdates,
  NEW_USER_TEMP_ID,
  processTeamInheritedDetails,
  removeTeam,
  updateManagerRole,
  updateProjectProductRole,
  updateProjectMetabaseAccess,
  updateUserSettings,
  UserDetailsType,
  userDetailUpdateInitialState,
  UserDetailUpdates,
  OrgRowSpanCalculatorType,
  removeProjectWithRoles,
} from './user-details-xstate-helper';
import { UserInterpreter } from './user.xstate';
import { getXstateUtils } from './xstate.utils';
import {
  addUsersToTeam,
  clearTeamState,
  discardTeamChanges,
  getProjectAvailableRoles,
  getTeamAccountProjectRoles,
  getTeamPrimaryAccountId,
  handleAddProjectToTeam,
  NEW_TEAM_TEMP_ID,
  removeTeamProjectWithRoles,
  removeUserFromTeam,
  teamDetailsInitialState,
  TeamDetailsType,
  teamDetailUpdateInitialState,
  TeamDetailUpdates,
  toggleTeamAllOrgsCollapseState,
  toggleTeamOrgCollapseState,
  updateTeamManagerRole,
  updateTeamProjectProductRole,
} from './team-details-xstate-helper';
import { TeamInfoDetailsFormValues } from '../pages/teams/team-details/team-info';
import {
  AgentAssistRoleNames,
  AnalyticsRoleNames,
  getRoleLevelForAccountRoles,
  handleOrgsDataProcessing,
  processAccountRoles,
  productsSortFunction,
} from './permission-details-xstate-helper';
import {
  fetchCanManageAccessByOrgId,
  filterProjectRolesUpdateByOrgAccess,
  getOrgCanManageAccess,
} from './org-access-helper';
import { resolveDeletedProjectName } from './project-delete-xstate-helper';
import {
  AccountProject,
  AccountRole,
  AccountTeams,
  ShortOrgType,
} from '../components/permissions-table/permissions-table.types';
import { ErrorTags } from '@pypestream/ui-patterns';
import { getAnalytics } from '../hooks/use-analytics';
import { PostHog } from 'posthog-js';

const { createInvokablePromise, createXstateHooks: createManagerXstateHooks } =
  getXstateUtils<
    ManagerContext,
    ManagerEvents,
    ManagerTypestates,
    ManagerState
  >();

export { createManagerXstateHooks };

export const treeUtils = new TreeUtils({
  idProp: 'id', // the key of a unique identifier for an object (source object)
  parentIdProp: 'parentId', // the key of a unique parent identifier (source object)
  childrenProp: 'children', // the key, where child nodes are stored (destination object tree)
});

// @todo: figure out why we can't easily (fully) cancel the subscription after running just once
let hasUpdateUserInfoSubRun = false;

const analytics = getAnalytics();

/**
 * PSTUDIO-3111: per-org authorization guard for user-details mutations. Each
 * edit event targets one org; the guard allows it only if the logged-in user
 * has backend canManageAccess for THAT org (resolved per-org into the viewed
 * user's state). Replaces the previous single home-org flag, which blocked
 * legitimate edits in child orgs (and over-permitted edits in orgs the user
 * cannot manage). Pure / side-effect-free, as XState guards must be.
 */
const canManageEventOrg = (
  ctx: ManagerContext,
  event: ManagerEvents
): boolean => {
  const orgs = ctx.selectedUser
    ? ctx.userDetails[ctx.selectedUser]?.state?.orgs
    : undefined;

  let orgId: string | null | undefined;
  switch (event.type) {
    case 'manager.userDetails.addProject':
      orgId = event.project?.accountId;
      break;
    case 'manager.userDetails.updateProjectProductRole':
    case 'manager.userDetails.removeProjectWithRoles':
    case 'manager.userDetails.updateManagerRole':
    case 'manager.userDetails.addTeam':
    case 'manager.userDetails.removeTeam': {
      ({ orgId } = event);
      break;
    }
    default:
      orgId = undefined;
  }

  return getOrgCanManageAccess(orgs, orgId);
};

/** Shared fetcher for the per-org canManageAccess fan-out (single definition
 * so the urql call is not re-wrapped at every handler). */
const fetchOrganizationMetadata = (vars: { accountId: string }) =>
  urqlGql.getOrganizationMetadata(vars);

export const managerMachine = Machine<
  ManagerContext,
  ManagerState,
  ManagerEvents
>({
  id: 'manager',
  predictableActionArguments: true,
  preserveActionOrder: true,
  invoke: {
    id: 'managerObserver',
    src: (ctx) => async (sendEvent: Sender<ManagerEvents>) => {
      // remove globalAppService subscription and featureFlags from manager context when managerProjectIdForContactCenter will be removed
      globalAppService.subscribe(async (state) => {
        const globalContext = state?.context;

        if (!globalContext) return;

        const { featureFlags } = globalContext;

        sendEvent({
          type: 'manager.setFeatureFlags',
          values: featureFlags,
        });
      });

      const userService = globalAppService.children.get(
        GLOBAL_APP_SUB_MACHINE_IDS.user
      ) as UserInterpreter;

      const userServiceSubscription = userService?.subscribe(async (state) => {
        const userContext = state?.context;

        const id = userContext?.user?.id;
        if (!id) return;

        if (hasUpdateUserInfoSubRun === false) {
          hasUpdateUserInfoSubRun = true;
          const defaultOrgId = userContext?.user?.defaultAccount?.defaultOrgId;
          const email = userContext?.user?.email;
          const status = userContext?.user?.status;
          const projectIdsAssignedThroughTeams = compact(
            userContext?.user?.assignedTeams
              ?.map(({ assignedProjects }) =>
                assignedProjects?.map((assignedProject) => assignedProject.id)
              )
              .flat()
          );

          sendEvent({
            type: 'manager.updateUserInfo',
            data: {
              ...ctx?.userInfo,
              id,
              // kratosId,
              defaultOrgId,
              email,
              status,
              isPypestreamEmployee:
                email?.endsWith('@pypestream.com') ||
                email?.endsWith('@pypestream.mailisk.net'),
              projectIds: projectIdsAssignedThroughTeams,
            },
          });
        }
      });

      if (hasUpdateUserInfoSubRun) {
        userServiceSubscription.unsubscribe();
      }
    },
  },
  context: {
    userInfo: {},
    selectedOrgId: undefined,
    projects: [],
    projectsNew: [],
    userProjectIds: [],
    errors: [],
    tools: initialTools,
    getProducts,
    routes: {
      home: '/',
      myAccount: '/my-account',
      users: '/users',
      teams: '/teams',
      roles: '/roles-management',
      projects: '/projects',
      orgs: '/child-orgs',
      logs: '/audit-logs',
      superAdmin: '/super-admin',
      admin: import.meta.env.FE_ADMIN_URL,
    },
    appletTemplates: [],
    languages: [],
    localizationSettingsConfig: undefined,
    timezones: [],
    users: [],
    teams: [],
    logs: {
      count: 0,
      rows: [],
    },
    logsCache: {},
    childOrgs: {
      currentOrg: undefined,
      rows: [],
      count: 0,
    },
    userDetails: {},
    teamDetails: {},
    orgDetails: undefined,
    projectDetails: undefined,
  },
  type: 'parallel',
  on: {
    loggedIn: {
      actions: assign((ctx) => {
        console.log('logged in!');
        // ctx.selectedOrgId = orgId;
        return ctx;
      }),
    },
    'manager.selectProject': {
      actions: assign((ctx, { id }) => {
        ctx.selectedProject = id;
        return ctx;
      }),
      cond: (ctx, event) =>
        event.type === 'manager.selectProject' &&
        event.id !== ctx.selectedProject,
    },
    'manager.updateUserInfo': {
      target: '#checkingUserInfo',
      actions: assign((ctx, { data: { projectIds, ...userInfo } }) => {
        ctx.userInfo = userInfo;
        ctx.userProjectIds = projectIds;
        return ctx;
      }),
    },
    'manager.createDbSnapshot': {
      target: '#dataBaseSnapshot.loading',
    },
    'manager.selectUser': {
      actions: assign((ctx, { id }) => {
        ctx.selectedUser = id;
        return ctx;
      }),
    },
    'manager.clearSelectedUser': {
      actions: assign((ctx) => {
        ctx.selectedUser = undefined;
        return ctx;
      }),
    },
    'manager.selectTeam': {
      actions: assign((ctx, { id }) => {
        ctx.selectedTeam = id;
        return ctx;
      }),
    },
    'manager.setFeatureFlags': {
      actions: assign((ctx, { values }) => {
        ctx.featureFlags = values;
        return ctx;
      }),
    },
    'manager.selectChildOrg': {
      actions: assign((ctx, { id }) => {
        ctx.selectedChildOrg = id;
        return ctx;
      }),
    },
    'manager.selectEnvironment': {
      actions: assign((ctx, { id }) => {
        ctx.selectedEnvironment = id;
        return ctx;
      }),
    },
    'manager.selectApplet': {
      actions: assign((ctx, { id }) => {
        ctx.selectedApplet = id;
        return ctx;
      }),
    },
    'manager.selectProjectModal': {
      actions: assign((ctx, { id }) => {
        ctx.selectedProjectModal = id;
        return ctx;
      }),
    },
    'manager.deselectProjectModal': {
      actions: assign((ctx) => {
        ctx.selectedProjectModal = undefined;
        return ctx;
      }),
    },
  },
  states: {
    orgRelated: {
      type: 'compound',
      initial: 'idle',
      states: {
        idle: {
          id: 'idle',
        },
        checkingUserInfo: {
          id: 'checkingUserInfo',
          always: [
            {
              target: 'ready',
              cond: (context, event) => context.userInfo?.id !== undefined,
            },
            {
              target: 'idle',
            },
          ],
        },
        ready: {
          id: 'ready',
          type: 'parallel',
          states: {
            currentOrg: {
              id: 'currentOrg',
              initial: 'notSelected',
              on: {
                'manager.selectOrgId': {
                  actions: assign((ctx, { orgId }) => {
                    ctx.selectedOrgId = orgId || ctx.userInfo.defaultOrgId;
                    ctx.selectedProjectModal = undefined;
                    return ctx;
                  }),
                  target: '.selected',
                },
              },
              states: {
                notSelected: {},
                selected: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (!ctx.selectedOrgId) return;

                      const { data, error } = await urqlGql.getAccountById({
                        id: ctx.selectedOrgId,
                      });

                      if (!data?.admin_?.accountById) {
                        throw new NotFoundError('Organization not found');
                      }

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      if (error instanceof NotFoundError) {
                        ctx.errors = [error];
                      }
                    },
                  }),
                },
              },
            },
            orgs: {
              initial: 'idle',
              id: 'orgs',
              on: {
                'manager.selectOrgId': {
                  target: '#orgLoading',
                },
              },
              states: {
                idle: {
                  // entry: () => {
                  //   console.log('ready to load org info!');
                  // },
                  always: {
                    target: 'loading',
                  },
                },
                loading: {
                  id: 'orgLoading',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      orgs: ManagerContext['orgs'];
                      defaultAuthMethods: ManagerContext['defaultAuthMethods'];
                    }> => {
                      const { data, error } = await urqlGql.getAccounts();

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      let orgs: Omit<Org, 'allChildAccounts'>[] = [];

                      const defaultAccount: Org | undefined =
                        data?.admin_?.currentUser?.defaultAccount;

                      if (defaultAccount) {
                        const { allChildAccounts, ...orgProps } =
                          defaultAccount;
                        orgs = [orgProps].concat(allChildAccounts || []);
                      }

                      return {
                        orgs,
                        defaultAuthMethods: data?.admin_?.defaultAuthMethods,
                      };
                    },
                    onDoneTarget: 'loaded',
                    onDoneAssignContext({ ctx, data }) {
                      if (data?.orgs) {
                        ctx.orgs = data.orgs;
                        ctx.defaultAuthMethods = data.defaultAuthMethods;
                      }
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
                loaded: {},
              },
            },
            orgDetails: {
              initial: 'idle',
              id: 'orgDetails',
              on: {
                'manager.loadOrg': {
                  target: '.loading',
                },
                'manager.addOrg': {
                  target: '.adding',
                },
                'manager.updateOrg': {
                  target: '.updating',
                },
                'manager.deleteOrg': {
                  target: '.deleting',
                },
              },
              states: {
                idle: {},
                loading: {
                  entry: assign((ctx) => {
                    ctx.orgDetails = undefined;
                    return ctx;
                  }),
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      orgDetails: ManagerContext['orgDetails'];
                    }> => {
                      if (event.type !== 'manager.loadOrg') return;

                      const { data, error } = await urqlGql.getAccount({
                        id: event.orgId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return {
                        orgDetails: data?.admin_?.accountById,
                      };
                    },
                    onDoneTarget: 'idle',
                    onDoneAssignContext({ ctx, data }) {
                      if (!data) return ctx;

                      ctx.orgDetails = data.orgDetails;
                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                adding: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      account: NonNullable<
                        CreateAccountMutation['admin_']
                      >['createAccount'];
                      callback: ((res: boolean) => void) | undefined;
                    }> => {
                      if (
                        event.type !== 'manager.addOrg' ||
                        !event.accountName ||
                        !ctx.selectedOrgId
                      )
                        return;

                      const {
                        accountName,
                        accountManagerId,
                        defaultAuthMethods,
                        accountIconId,
                        customAuthMethods,
                      } = event;

                      const { data, error } = await urqlGql.createAccount({
                        parentAccountId: ctx.selectedOrgId,
                        accountName,
                        accountManagerId,
                        accountIconId,
                        defaultAuthMethods,
                        customAuthMethods,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return {
                        account: data?.admin_?.createAccount,
                        callback: event.callback,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (!data || !data.account) {
                        return ctx;
                      }
                      ctx.orgs?.push({ id: data.account.id });
                      return ctx;
                    },
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `The organization ${data?.account?.name} created successfully`,
                            autoClose: 3000,
                          });
                          if (data && data.callback) {
                            data.callback(true);
                          }
                        },
                      },
                    ],
                    onDoneTarget: 'added',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                added: {
                  after: {
                    100: 'idle',
                  },
                },
                updating: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      updateAccount: NonNullable<
                        UpdateAccountMutation['admin_']
                      >['updateAccount'];
                      callback: ((res: boolean) => void) | undefined;
                    }> => {
                      if (
                        event.type !== 'manager.updateOrg' ||
                        !ctx.selectedOrgId
                      )
                        return;

                      const {
                        name,
                        accountManagerId,
                        generalSettings,
                        resourceLimitSettings,
                        accountIconId,
                        customAuthMethods,
                        accountAuthMethodSettings,
                      } = event;
                      const { data, error } = await urqlGql.updateAccount({
                        id: ctx.selectedOrgId,
                        name,
                        accountManagerId,
                        generalSettings,
                        resourceLimitSettings,
                        accountIconId,
                        ...(customAuthMethods && {
                          customAuthMethods,
                        }),
                        ...(accountAuthMethodSettings && {
                          accountAuthMethodSettings,
                        }),
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return {
                        updateAccount: data?.admin_?.updateAccount,
                        callback: event.callback,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined && !!data.updateAccount) {
                        // update childOrgs context
                        if (
                          ctx.childOrgs.currentOrg &&
                          ctx.childOrgs.currentOrg?.id === data.updateAccount.id
                        ) {
                          ctx.childOrgs.currentOrg.name =
                            data.updateAccount.name;
                          ctx.childOrgs.currentOrg.pictureFile =
                            data.updateAccount.pictureFile;
                        }
                      }
                    },
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `Organization ${data?.updateAccount?.name} updated successfully`,
                            autoClose: 3000,
                          });
                          if (data && data.callback) {
                            data.callback(true);
                          }
                        },
                      },
                    ],
                    onDoneTarget: 'updated',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating org's data: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                updated: {
                  after: {
                    100: 'idle',
                  },
                },
                deleting: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<{
                      id: Account['id'];
                      callback: ((res: boolean) => void) | undefined;
                    } | void> => {
                      if (event.type !== 'manager.deleteOrg' || !event.id)
                        return;

                      if (!ctx.selectedOrgId) {
                        throw new Error(
                          'missing info needed to delete project! (projectId or selectedOrgId)'
                        );
                      }

                      if (ctx.userInfo.defaultOrgId === ctx.selectedOrgId) {
                        throw new Error('cannot delete default organization!');
                      }

                      const { data, error } = await urqlGql.deleteAccount({
                        id: event.id,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                      return {
                        id: event.id,
                        callback: event.callback,
                      };
                    },
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `The organization deleted successfully`,
                            autoClose: 3000,
                          });
                          if (data && data.callback) {
                            data.callback(true);
                          }
                        },
                      },
                    ],
                    onDoneAssignContext({ ctx, data }) {
                      if (!data || !ctx.orgs) return ctx;

                      ctx.orgs = ctx.orgs.filter((org) => org.id !== data.id);
                      return ctx;
                    },
                    onDoneTarget: 'deleted',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                deleted: {
                  after: {
                    100: 'idle',
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            currentProject: {
              id: 'currentProject',
              initial: 'notSelected',
              on: {
                'manager.selectProject': {
                  actions: assign((ctx, { id }) => {
                    ctx.selectedProject = id;
                    return ctx;
                  }),
                  target: '.selected',
                },
              },
              states: {
                notSelected: {},
                selected: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (!ctx.selectedProject) return;

                      const { data, error } = await urqlGql.getProjectById({
                        id: ctx.selectedProject,
                      });

                      if (!data?.admin_?.projectById) {
                        throw new NotFoundError('Project not found');
                      }

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      if (error instanceof NotFoundError) {
                        ctx.errors = [error];
                      }
                    },
                  }),
                },
              },
            },
            projects: {
              id: 'projects',
              initial: 'idle',
              on: {
                'manager.loadProjects': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  id: 'loadingProjects',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      _event
                    ): Promise<{
                      projects: ManagerContext['projects'];
                      userProjectIds: string[];
                    }> => {
                      if (
                        ctx.userInfo.id === undefined ||
                        ctx.selectedOrgId === undefined
                      ) {
                        throw Error('missing info needed to get projects!');
                      }

                      const { data, error } = await urqlGql.getProjects({
                        userId: ctx.userInfo.id,
                        accountId: ctx.selectedOrgId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      const allProjects = compact(data?.admin_?.projects?.rows);
                      const userProjectIds = compact(
                        data?.admin_?.userProjects?.rows
                      );

                      return {
                        projects: allProjects,
                        userProjectIds: userProjectIds.length
                          ? uniq(
                              compact(
                                userProjectIds
                                  .map((item) => item.project?.id)
                                  .concat(ctx.userProjectIds)
                              )
                            )
                          : [...ctx.userProjectIds],
                      };
                    },
                    onDoneTarget: 'loaded',
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.projects = data.projects;
                        ctx.userProjectIds = data.userProjectIds;
                      }

                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user projects: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  on: {
                    'manager.selectOrgId': {
                      actions: assign((ctx) => {
                        ctx.projects = [];
                        return ctx;
                      }),
                      target: 'loading',
                    },
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            projectDetails: {
              initial: 'idle',
              on: {
                'manager.loadProject': '.loading',
                'manager.addProject': '.adding',
                'manager.updateProject': '.updating',
                'manager.deleteProject': '.deleting',
                'manager.userInfo.fetchingComplete': '.transforming',
              },
              states: {
                idle: {},
                reloadAfterDelay: {
                  id: 'transformProjectsAfterDelay',
                  after: {
                    50: {
                      actions: [
                        raise('manager.loadNewProjects'),
                        raise('manager.loadProjects'),
                      ],
                      target: 'idle',
                    },
                  },
                },
                projectDeleted: {
                  after: {
                    50: {
                      actions: [
                        raise('manager.loadNewProjects'),
                        raise('manager.loadProjects'),
                      ],
                      target: 'idle',
                    },
                  },
                },
                loading: {
                  entry: assign((ctx) => {
                    ctx.projectDetails = undefined;
                    return ctx;
                  }),
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | NonNullable<
                          GetProjectDetailsQuery['admin_']
                        >['projectById']
                      | undefined
                    > => {
                      if (
                        event.type !== 'manager.loadProject' ||
                        !event.projectId
                      )
                        return;

                      const { data, error } = await urqlGql.getProjectDetails({
                        id: event.projectId,
                        withEnvironments:
                          !!ctx.featureFlags?.['environments-and-web-applets'],
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return data?.admin_?.projectById;
                    },
                    onDoneTarget: 'beforeTransforming',
                    onDoneAssignContext({ ctx, data }) {
                      if (!data) return ctx;
                      ctx.projectDetails = data;
                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user projects: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                beforeTransforming: {
                  always: [
                    {
                      target: 'transforming',
                      cond: (ctx) =>
                        Boolean(
                          ctx.userInfo.id !== undefined &&
                            ctx.userInfo?.productRoles &&
                            (ctx.products || []).length > 0
                        ),
                    },
                  ],
                },
                transforming: {
                  entry: assign((ctx) => {
                    if (!ctx.projectDetails) return ctx;

                    const { conditionalProducts } = getProducts({
                      project: ctx.projectDetails,
                      ctx,
                    });

                    ctx.projectDetails = {
                      ...ctx.projectDetails,
                      conditionalProducts,
                    };

                    return ctx;
                  }),
                  always: {
                    target: 'idle',
                  },
                },
                adding: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      projects: ManagerContext['projects'];
                      userInfo: ManagerContext['userInfo'];
                      newProjectId: string | undefined;
                      callback: ((res: boolean) => void) | undefined;
                    }> => {
                      if (
                        event.type !== 'manager.addProject' ||
                        !event.name ||
                        !ctx.selectedOrgId
                      )
                        return;

                      const {
                        timeZoneId,
                        name,
                        productIds,
                        description,
                        projectEnvironmentConfigs,
                        projectIconId,
                      } = event;

                      const { data, error } = await urqlGql.createProject({
                        name,
                        accountId: ctx.selectedOrgId,
                        productIds: productIds || [],
                        description: description || '',
                        timeZoneId: timeZoneId || undefined,
                        projectEnvironmentConfigs,
                        projectIconId: projectIconId
                          ? transformProfilePicture(projectIconId)
                          : '',
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      if (data?.admin_?.createProject === undefined) {
                        return {
                          projects: ctx.projects,
                          userInfo: ctx.userInfo,
                          newProjectId: undefined,
                          callback: event.callback,
                        };
                      }

                      const projects: ManagerContext['projects'] = [
                        {
                          // __typename: 'UserProject',
                          // accountId: ctx.selectedOrgId,
                          // project: data.admin_.createProject,
                          projectId: data.admin_.createProject.id,
                          accountId: data.admin_.createProject.accountId,
                          name: data.admin_.createProject.name,
                          description: data.admin_.createProject.description,
                          picture: data.admin_.createProject.picture,
                          updatedAt: new Date().toISOString(),
                          createdAt: new Date().toISOString(),
                          pictureFile: data.admin_.createProject.pictureFile,
                          projectProductSettings:
                            data.admin_.createProject.projectProductSettings,
                          users: data.admin_.createProject.users,
                        },
                        ...(ctx.projects || []),
                      ];

                      const products = ctx.getProducts({
                        ctx,
                      });

                      const projectProducts =
                        products.conditionalProducts.filter((product) =>
                          productIds.includes(product.productId)
                        );

                      let userInformation = ctx.userInfo;

                      // update userInfo with new project for agentAssist product
                      if (
                        projectProducts.find(
                          (product) => product.name === ProductName.AgentAssist
                        ) &&
                        userInformation.productRoles?.agentAssistRolesSource
                      ) {
                        userInformation = {
                          ...userInformation,
                          productRoles: {
                            ...userInformation.productRoles,
                            agentAssistRolesSource: [
                              ...userInformation.productRoles
                                .agentAssistRolesSource,
                              {
                                project: {
                                  id: data.admin_.createProject.id,
                                  name: data.admin_.createProject.name,
                                  accountId:
                                    data.admin_.createProject.accountId,
                                },
                              },
                            ],
                          },
                        };
                      }

                      // update userInfo with new project for analytics product
                      if (
                        projectProducts.find(
                          (product) => product.name === ProductName.Analytics
                        ) &&
                        userInformation.productRoles?.analyticsRolesSource
                      ) {
                        userInformation = {
                          ...userInformation,
                          productRoles: {
                            ...userInformation.productRoles,
                            analyticsRolesSource: [
                              ...userInformation.productRoles
                                .analyticsRolesSource,
                              {
                                project: {
                                  id: data.admin_.createProject.id,
                                  name: data.admin_.createProject.name,
                                  accountId:
                                    data.admin_.createProject.accountId,
                                },
                              },
                            ],
                          },
                        };
                      }

                      if (
                        projectProducts.find(
                          (product) => product.name === ProductName.ProStudio
                        ) &&
                        userInformation.productRoles?.proStudioRolesSource
                      ) {
                        userInformation = {
                          ...userInformation,
                          productRoles: {
                            ...userInformation.productRoles,
                            proStudioRolesSource: [
                              ...userInformation.productRoles
                                .proStudioRolesSource,
                              {
                                project: {
                                  id: data.admin_.createProject.id,
                                  name: data.admin_.createProject.name,
                                  accountId:
                                    data.admin_.createProject.accountId,
                                },
                              },
                            ],
                          },
                        };
                      }
                      return {
                        projects,
                        userInfo: userInformation,
                        newProjectId: data.admin_.createProject.id,
                        callback: event.callback,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.projects = data.projects;
                        ctx.userInfo = data.userInfo;

                        if (
                          data.newProjectId &&
                          data.newProjectId !== undefined
                        ) {
                          ctx.userProjectIds.push(data.newProjectId);
                        }
                        if (data.callback) {
                          data.callback(true);
                        }
                      }
                    },
                    onDoneTarget: 'reloadAfterDelay',
                    onErrorTarget: 'error',
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                updating: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      nextProjectsState: ManagerContext['projects'];
                      updatedProject?: NonNullable<
                        UpdateProjectMutation['admin_']
                      >['updateProject'];
                    }> => {
                      if (event.type !== 'manager.updateProject') return;

                      if (event.projectId === undefined) return;

                      const { data, error } = await urqlGql.updateProject({
                        projectId: event.projectId,
                        description: event.description || '',
                        name: event.name || '',
                        timeZoneId: event.timeZoneId || undefined,
                        productIds: event.productIds || [],
                        projectIconId: event.projectIconId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      const updatedProject = data?.admin_?.updateProject;

                      if (
                        ctx.projects?.length === 0 ||
                        ctx.projects === undefined
                      ) {
                        return {
                          nextProjectsState: [],
                          updatedProject,
                        };
                      }

                      if (ctx.featureFlags?.['environments-and-web-applets']) {
                        const updateChannels = await Promise.all(
                          (event.environmentConfig || []).map(
                            async ({ name, description, domains }) => {
                              const projectEnvironmentConfigId =
                                ctx.projectDetails?.projectEnvironmentConfig?.find(
                                  ({ environment }) => {
                                    return environment?.name === name;
                                  }
                                )?.id || '';

                              return urqlGql.updateProjectEnvironmentConfig({
                                projectId: event.projectId || '',
                                description,
                                domains,
                                projectEnvironmentConfigId,
                              });
                            }
                          )
                        );

                        updateChannels.map(
                          ({ data: channelsData, error: channelsError }) => {
                            if (haveErrors(channelsData)) {
                              throw new Error(
                                channelsData.errors?.[0]?.message
                              );
                            }

                            if (channelsError) {
                              throw new Error(channelsError.message);
                            }
                          }
                        );
                      }

                      const nextProjectsState = ctx.projects.map((item) => {
                        if (item.projectId === updatedProject?.projectId) {
                          return {
                            ...item,
                            ...updatedProject,
                          };
                        }
                        return item;
                      });

                      return { nextProjectsState, updatedProject };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (!data) return ctx;

                      ctx.projects = data.nextProjectsState;

                      if (ctx.projectDetails) {
                        ctx.projectDetails = {
                          ...ctx.projectDetails,
                          ...data.updatedProject,
                        };
                      }

                      return ctx;
                    },
                    onDoneTarget: 'reloadAfterDelay',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating project's data: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `Project ${data?.updatedProject?.name} updated successfully`,
                            autoClose: 3000,
                          });
                        },
                      },
                      (ctx, { data }) => {
                        if (
                          data?.updatedProject?.users?.rows?.find(
                            (user) => user.id === ctx.userInfo.id
                          )
                        ) {
                          sendManagerEvent({
                            type: 'manager.refetchUserSettings',
                            orgId: ctx.selectedOrgId,
                          });
                        }
                      },
                    ],
                  }),
                },
                deleting: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      projects: ManagerContext['projects'];
                      event: ManagerEvents;
                      projectName?: string;
                    }> => {
                      if (event.type !== 'manager.deleteProject') return;

                      if (!event.projectId || !ctx.selectedOrgId) {
                        throw new Error(
                          'missing info needed to delete project! (projectId or selectedOrgId)'
                        );
                      }

                      const { data, error } = await urqlGql.deleteProject({
                        projectId: event.projectId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      // The homepage renders `projectsNew` (Hasura); the legacy
                      // `projects` array is not always populated there, and the
                      // project-details page (the only place with a Delete
                      // button) populates neither — so look the deleted
                      // project's name up in both lists and fall back to the
                      // loaded `projectDetails`. Without this the success toast
                      // showed "Project undefined deleted successfully".
                      const deletedProjectName = resolveDeletedProjectName(
                        event.projectId,
                        {
                          projectsNew: ctx.projectsNew,
                          projects: ctx.projects,
                          projectDetails: ctx.projectDetails,
                        }
                      );
                      const filteredProjects = ctx.projects?.filter(
                        (project) => project.projectId !== event.projectId
                      );

                      return {
                        projects: filteredProjects,
                        event,
                        projectName: deletedProjectName,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.projects = data.projects;
                      }
                    },
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          if (data?.event?.type !== 'manager.deleteProject')
                            return;

                          sendUserMessage({
                            type: 'success',
                            text: `Project ${data.projectName} deleted successfully`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onDoneTarget: 'projectDeleted',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                  }),
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },

            projectsNew: {
              id: 'projectsNew',
              initial: 'idle',
              on: {
                'manager.loadNewProjects': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  entry: assign((ctx) => {
                    ctx.projectsNew = [];
                    return ctx;
                  }),
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      _event
                    ): Promise<{
                      projects: ManagerContext['projectsNew'];
                    }> => {
                      if (
                        ctx.userInfo.id === undefined ||
                        ctx.selectedOrgId === undefined
                      ) {
                        throw Error('missing info needed to get projects!');
                      }

                      const { data, error } =
                        await urqlGql.getUserProjectsHasura({
                          userId: ctx.userInfo.id,
                          accountId: ctx.selectedOrgId,
                        });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                      const mappedProjects: ProjectNew[] = [];

                      if (data?.tenancy_user_accounts.length) {
                        data?.tenancy_user_accounts.forEach(
                          ({ user, user_account_projects }) => {
                            const projectsByTeams: ProjectNew[] = [];
                            const projectsByAccount = user_account_projects.map(
                              ({ project }) => {
                                return {
                                  ...project,
                                };
                              }
                            );
                            user._user_teams.forEach(({ team }) => {
                              team.team_accounts.forEach(
                                ({ team_account_projects }) => {
                                  team_account_projects.forEach(
                                    ({ project }) => {
                                      projectsByTeams.push({
                                        ...project,
                                      });
                                    }
                                  );
                                }
                              );
                            });
                            mappedProjects.push(
                              ...[...projectsByAccount, ...projectsByTeams]
                            );
                          }
                        );
                      }

                      return {
                        projects: uniqBy(
                          orderBy(
                            mappedProjects,
                            (project) => project.name.toLowerCase(),
                            'asc'
                          ),
                          'id'
                        ),
                      };
                    },
                    onDoneTarget: 'beforeTransforming',
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.projectsNew = data.projects;
                      }
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user projects: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                beforeTransforming: {
                  always: [
                    {
                      target: 'transforming',
                      cond: (ctx) =>
                        Boolean(
                          ctx.userInfo.id !== undefined &&
                            ctx.userInfo?.productRoles &&
                            (ctx.products || []).length > 0
                        ),
                    },
                  ],
                },
                transforming: {
                  entry: assign((ctx) => {
                    const projectsWithAvailableProduct =
                      ctx.projectsNew?.filter(
                        ({ projectProductSettings }) =>
                          projectProductSettings?.length
                      ) || [];

                    const projectWithAvailableProduct =
                      projectsWithAvailableProduct.length === 1
                        ? projectsWithAvailableProduct[0]
                        : undefined;

                    const projectWithAvailableAgentAssistProduct =
                      ctx.projectsNew?.find(({ id }) => {
                        const agentAssistRolesSource = (
                          ctx.userInfo?.productRoles?.agentAssistRolesSource ||
                          []
                        ).filter(
                          ({ project: p }) => p?.accountId === ctx.selectedOrgId
                        );

                        return agentAssistRolesSource.length === 1
                          ? agentAssistRolesSource[0]?.project?.id === id
                          : undefined;
                      });

                    const projectWithAvailableAnalyticsProduct =
                      ctx.projectsNew?.find(({ id }) => {
                        const analyticsRolesSource = (
                          ctx.userInfo?.productRoles?.analyticsRolesSource || []
                        ).filter(
                          ({ project: p }) => p?.accountId === ctx.selectedOrgId
                        );

                        return analyticsRolesSource.length === 1
                          ? analyticsRolesSource[0]?.project?.id === id
                          : undefined;
                      });

                    const projectWithAvailableProStudioProduct =
                      ctx.projectsNew?.find(({ id }) => {
                        const proStudioRolesSource = (
                          ctx.userInfo?.productRoles?.proStudioRolesSource || []
                        ).filter(
                          ({ project: p }) => p?.accountId === ctx.selectedOrgId
                        );

                        return proStudioRolesSource.length === 1
                          ? proStudioRolesSource[0]?.project?.id === id
                          : undefined;
                      });

                    const tools = [
                      projectWithAvailableProduct,
                      projectWithAvailableAgentAssistProduct,
                      projectWithAvailableAnalyticsProduct,
                      projectWithAvailableProStudioProduct,
                    ].map((projectWithProduct, index) => {
                      const { products: _tools } = ctx.getProducts({
                        project: projectWithProduct,
                        ctx,
                        isNewProjects: true,
                      });

                      return _tools[index];
                    });

                    const projectsNew = ctx.projectsNew?.map((p) => {
                      const {
                        selectedProjectURL,
                        conditionalProducts,
                        hasAvailableProduct,
                      } = ctx.getProducts({
                        project: p,
                        ctx,
                        isNewProjects: true,
                      });

                      return {
                        ...p,
                        selectedProjectURL,
                        conditionalProducts,
                        hasAvailableProduct,
                      };
                    });

                    return {
                      ...ctx,
                      tools,
                      projectsNew,
                    };
                  }),
                  always: {
                    target: 'loaded',
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
                loaded: {
                  on: {
                    'manager.selectOrgId': {
                      target: 'loading',
                    },
                  },
                },
              },
            },

            applets: {
              id: 'applets',
              initial: 'idle',
              states: {
                idle: {
                  entry: (ctx) => {
                    // console.log('ready to load projects info!', ctx.selectedOrgId);
                  },
                  always: [
                    {
                      target: 'loading',
                      cond: (ctx, event, data) => {
                        const cond = Boolean(
                          ctx.selectedProject !== undefined &&
                            ctx.userInfo.id !== undefined &&
                            !ctx.applets
                        );

                        return cond;
                      },
                    },
                  ],
                },
                loading: {
                  id: 'loadingApplets',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      _event
                    ): Promise<{
                      applets: ManagerContext['applets'];
                    }> => {
                      if (
                        ctx.userInfo.id === undefined ||
                        ctx.selectedProject === undefined
                      ) {
                        throw Error('missing info needed to get projects!');
                      }

                      const { data, error } =
                        await urqlGql.getAppletInstancesByProjectId({
                          projectId: ctx.selectedProject,
                        });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      const allAppletInstances =
                        compact(
                          flatten(
                            compact(
                              data?.admin_?.projectById
                                ?.projectEnvironmentConfig
                            )?.map((prcc) => prcc.appletInstances)
                          )
                        ) || [];

                      return {
                        applets: allAppletInstances,
                      };
                    },
                    onDoneTarget: 'loaded',
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.applets = data.applets;
                      }

                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user projects: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
                adding: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | void
                      | NonNullable<
                          CreateAppletInstanceMutation['admin_']
                        >['createAppletInstance']
                    > => {
                      if (
                        event.type !== 'manager.addApplet' ||
                        !event.name ||
                        !ctx.selectedOrgId
                      )
                        return;

                      const {
                        description,
                        name,
                        projectEnvironmentConfigId,
                        appletVersionId,
                      } = event;

                      const { data, error } =
                        await urqlGql.createAppletInstance({
                          name: name,
                          projectEnvironmentConfigId:
                            projectEnvironmentConfigId,
                          description: description || '',
                          // appletVersionId: event.latestVersion.id,
                          appletVersionId: appletVersionId,
                        });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                      return data?.admin_?.createAppletInstance;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (!data) {
                        throw new Error(
                          'Returned data from createAppletInstance is undefined'
                        );
                      }
                      ctx.applets?.push(data);
                    },
                    onDoneTarget: 'transformAfterDelay',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                transformAfterDelay: {
                  id: 'transformAppletsAfterDelay',
                  after: {
                    50: {
                      target: 'loaded',
                    },
                  },
                },
                deleting: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<AppletInstance['id'] | void> => {
                      if (event.type !== 'manager.deleteApplet' || !event.id)
                        return;

                      const { data, error } =
                        await urqlGql.deleteAppletInstance({
                          deleteAppletInstanceId: event.id,
                        });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                      return event.id;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      ctx.applets = ctx.applets?.filter((a) => a.id !== data);
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error fetching user orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },

                updating: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | void
                      | NonNullable<
                          UpdateAppletInstanceMutation['admin_']
                        >['updateAppletInstance']
                    > => {
                      if (event.type !== 'manager.updateApplet') return;

                      if (event.updateAppletInstanceId === undefined) return;

                      const { type, ...variables } = event;
                      const { data, error } =
                        await urqlGql.updateAppletInstance(variables);

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return data?.admin_?.updateAppletInstance;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        const index = ctx.applets?.findIndex(
                          (a) => a.id === data.id
                        );
                        if (index === undefined) {
                          throw new Error(
                            'Returned applet from UpdateAppletInstance Mutation is not found'
                          );
                        }
                        if (index >= 0) {
                          ctx.applets = ctx.applets?.with(index, data);
                        }
                      }
                    },
                    // onDoneTarget: 'idle',
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating project's data: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `Applet ${data?.name} updated successfully`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  id: 'AppletsLoaded',
                  on: {
                    'manager.addApplet': 'adding',
                    'manager.deleteApplet': 'deleting',
                    'manager.updateApplet': 'updating',
                  },
                },
              },
            },

            // Shouldn't live in this state machine (instead under User machine); shouldn't require us to pass in the accountId. Even if it did, it should be the accountId under our user account, not the accountId of the currently selected org
            userInfo: {
              id: 'userInfo',
              initial: 'idle',
              states: {
                idle: {
                  always: [
                    {
                      target: 'fetching',
                      cond: (context) =>
                        Boolean(
                          !context.userInfo?.listOfAuthMethods &&
                            context.userInfo?.id !== undefined &&
                            context.selectedOrgId
                        ),
                    },
                  ],
                  on: {
                    'manager.refetchUserSettings': {
                      target: '#userInfo.fetching',
                    },
                    'manager.connectingAuthMethod': {
                      target: '#userInfo.connectingAuthMethod',
                    },
                  },
                },
                fetching: {
                  id: 'fetching',
                  invoke: createInvokablePromise({
                    src: async (ctx, event) => {
                      const {
                        userInfo: { id: userId, defaultOrgId },
                        selectedOrgId: accountId,
                      } = ctx;

                      const selectedOrgId =
                        event.type === 'manager.refetchUserSettings'
                          ? event.orgId
                          : accountId;

                      if (!userId || !selectedOrgId) {
                        throw new Error('User id or accountId not provided');
                      }

                      const { data, error } = await urqlGql
                        .getUserSettings({
                          accountId: defaultOrgId || selectedOrgId,
                          userId,
                        })
                        .toPromise();

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      const nextUserInfoState: ManagerContext['userInfo'] = {
                        ...ctx.userInfo,
                        status:
                          data?.admin_?.currentUser?.status ||
                          ctx.userInfo.status,
                        productRoles: data?.admin_?.userProductRoles || {},
                        canManageAccess: Boolean(
                          data?.admin_?.organizationMetadata?.canManageAccess
                        ),
                        defaultLanguage:
                          data?.admin_?.currentUser?.settings?.defaultLanguage?.split(
                            '-'
                          )?.[0],
                        listOfAuthMethods:
                          data?.admin_?.currentUser?.listOfAuthMethods,
                      };

                      if (nextUserInfoState.defaultLanguage) {
                        await changeLanguage(nextUserInfoState.defaultLanguage);
                      }

                      return {
                        nextUserInfoState,
                        event,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.userInfo = data.nextUserInfoState;
                      }
                    },
                    onDoneTarget: 'idle',
                    onDoneActions: [
                      (ctx, { data }) => {
                        if (
                          data?.event?.type === 'manager.refetchUserSettings'
                        ) {
                          sendManagerEvent({
                            type: 'manager.userInfo.fetchingComplete',
                          });
                        }
                      },
                    ],
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading My Account data via graphql: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                connectingAuthMethod: {
                  id: 'connectingAuthMethod',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<void | {
                      connectAuthMethod: NonNullable<
                        ConnectAuthMethodMutation['admin_']
                      >['connectAuthMethod'];
                      authMethodName: string;
                      accountAuthMethodSettingsId: string;
                    }> => {
                      if (event.type !== 'manager.connectingAuthMethod') return;
                      const { userId, accountAuthMethodSettingsId } = event;
                      if (!userId || !accountAuthMethodSettingsId) return;
                      const {
                        data: connectAuthMethodDetails,
                        error: connectAuthMethodError,
                      } = await urqlGql.connectAuthMethod({
                        userId,
                        accountAuthMethodSettingsId,
                      });

                      if (haveErrors(connectAuthMethodDetails)) {
                        throw new Error(
                          connectAuthMethodDetails.errors?.[0]?.message
                        );
                      }

                      if (connectAuthMethodError) {
                        throw new Error(connectAuthMethodError.message);
                      }

                      if (connectAuthMethodDetails?.admin_?.connectAuthMethod) {
                        return {
                          connectAuthMethod:
                            connectAuthMethodDetails?.admin_?.connectAuthMethod,
                          authMethodName: event.authMethodName,
                          accountAuthMethodSettingsId,
                        };
                      }
                      throw new Error(
                        `Failed to set up login method - ${event.authMethodName}`
                      );
                    },
                    onDoneTarget: 'idle',
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, { data }) {
                          sendUserMessage({
                            type: 'success',
                            text: `Login method '${data?.authMethodName}' set up successfully`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onDoneAssignContext({ ctx, data }) {
                      if (
                        data !== undefined &&
                        ctx.userInfo?.listOfAuthMethods
                      ) {
                        ctx.userInfo.listOfAuthMethods =
                          ctx.userInfo.listOfAuthMethods?.map((authMethod) => {
                            if (
                              authMethod.accountAuthMethodSettingsId ===
                              data.accountAuthMethodSettingsId
                            ) {
                              return {
                                ...authMethod,
                                isActive: true,
                              };
                            }
                            return authMethod;
                          });
                      }
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: event.data.message,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },

            // shouldn't exist
            routes: {
              id: 'routes',
              initial: 'idle',
              on: {
                'manager.selectOrgId': {
                  target: '#updatingRoutes',
                },
              },
              states: {
                idle: {
                  always: {
                    target: 'updating',
                    cond: (context) => context.selectedOrgId !== undefined,
                  },
                },
                updating: {
                  id: 'updatingRoutes',
                  entry: assign((ctx) => {
                    const { selectedOrgId, routes, userInfo } = ctx;
                    const { admin: adminBaseURL } = routes;
                    const adminURL = `${adminBaseURL}/organization/${selectedOrgId}`;
                    const basePath = selectedOrgId
                      ? `/organization/${selectedOrgId}`
                      : '';
                    const defaultOrgPath = userInfo
                      ? `/organization/${userInfo?.defaultOrgId}`
                      : '';

                    ctx.routes = {
                      ...ctx.routes,
                      home: basePath || '/',
                      // PSTUDIO-4095: My Account is its own surface, no longer
                      // the Users route under another URL. It stays anchored to
                      // the user's DEFAULT org, exactly as the redirect it
                      // replaces did (`${defaultOrgPath}/users/${userInfo?.id}`)
                      // — the page shows the user's own profile and loads its
                      // Roles tree from that org, so pointing it at whichever
                      // org happens to be on screen would change what the table
                      // contains rather than just where the link goes.
                      myAccount: `${defaultOrgPath}/my-account`,
                      projects: `${basePath}/projects`,
                      users: `${basePath}/users`,
                      teams: `${basePath}/teams`,
                      roles: `${adminURL}/roles-management`,
                      orgs: `${basePath}/child-orgs`,
                      logs: `${basePath}/audit-logs`,
                      superAdmin: `${basePath}/super-admin`,
                    };

                    return ctx;
                  }),
                  onDone: 'updated',
                },
                updated: {
                  on: {
                    'manager.selectOrgId': {
                      target: '#updatingRoutes',
                    },
                  },
                },
              },
            },
            currentUser: {
              id: 'currentUser',
              initial: 'notSelected',
              on: {
                'manager.selectUser': {
                  actions: immerAssign((ctx, { id }) => {
                    ctx.selectedUser = id;
                  }),
                  target: '.selected',
                },
              },
              states: {
                notSelected: {},
                selected: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (
                        !ctx.selectedUser ||
                        // Don't load user details for NEW_USER_TEMP_ID which is used with user invite flow
                        ctx.selectedUser === NEW_USER_TEMP_ID
                      )
                        return;

                      const { data, error } = await urqlGql.getUserById({
                        id: ctx.selectedUser,
                      });

                      if (!data?.admin_?.userById) {
                        throw new NotFoundError('User not found');
                      }

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      if (error instanceof NotFoundError) {
                        ctx.errors = [error];
                      }
                    },
                  }),
                },
              },
            },
            users: {
              id: 'users',
              initial: 'idle',
              on: {
                'manager.users.loadUsers': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<ManagerContext['users'] | undefined> => {
                      if (ctx.selectedOrgId === undefined) {
                        throw Error('Missing organization to load users!');
                      }

                      const { data, error } = await urqlGql.getUsers({
                        accountId: ctx.selectedOrgId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return data?.admin_?.users?.rows;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.users = data;
                      }
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading users: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  on: {
                    'manager.selectOrgId': {
                      actions: assign((ctx) => {
                        ctx.users = [];
                        return ctx;
                      }),
                      target: 'loading',
                    },
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            currentTeam: {
              id: 'currentTeam',
              initial: 'notSelected',
              on: {
                'manager.selectTeam': {
                  actions: assign((ctx, { id }) => {
                    ctx.selectedTeam = id;
                    return ctx;
                  }),
                  target: '.selected',
                },
              },
              states: {
                notSelected: {},
                selected: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (
                        !ctx.selectedTeam ||
                        ctx.selectedTeam === NEW_TEAM_TEMP_ID
                      )
                        return;

                      const { data, error } = await urqlGql.getTeamById({
                        id: ctx.selectedTeam,
                      });

                      if (!data?.admin_?.teamById) {
                        throw new NotFoundError('Team not found');
                      }

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      if (error instanceof NotFoundError) {
                        ctx.errors = [error];
                      }
                    },
                  }),
                },
              },
            },
            teams: {
              id: 'teams',
              initial: 'idle',
              on: {
                'manager.teams.loadTeams': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<ManagerContext['teams'] | undefined> => {
                      if (ctx.selectedOrgId === undefined) {
                        throw Error('Missing organization to load teams!');
                      }

                      const { data, error } = await urqlGql.getTeams({
                        accountId: ctx.selectedOrgId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return data?.admin_?.teams?.rows;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.teams = data;
                      }
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading teams: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  on: {
                    'manager.selectOrgId': {
                      actions: assign((ctx) => {
                        ctx.teams = [];
                        return ctx;
                      }),
                      target: 'loading',
                    },
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            logs: {
              initial: 'idle',
              on: {
                'manager.logs.loadLogs': {
                  target: '#loadLogs',
                },
                'manager.selectOrgId': {
                  actions: assign((ctx) => {
                    ctx.logs = {
                      count: 0,
                      rows: [],
                    };
                    return ctx;
                  }),
                  target: '#loadLogs',
                },
              },
              states: {
                idle: {},
                loading: {
                  id: 'loadLogs',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | {
                          data: ManagerContext['logs'] | undefined;
                          queryMetadata: QueryMetadata;
                        }
                      | undefined
                    > => {
                      if (event.type !== 'manager.logs.loadLogs') return;

                      if (ctx.selectedOrgId === undefined) {
                        throw Error('Missing organization to load logs!');
                      }

                      const { queryMetadata, invalidateCache } = event;
                      const { logsCache } = ctx;
                      const cacheKey = `${ctx.selectedOrgId}_${queryMetadata.skipPages}_${queryMetadata.pageSize}`;

                      if (logsCache[cacheKey] && !invalidateCache) {
                        return {
                          data: logsCache[cacheKey],
                          queryMetadata,
                        };
                      }

                      if (invalidateCache && logsCache[cacheKey]) {
                        const { cacheKey: _, ...newLogsCache } = logsCache;
                        ctx.logsCache = { ...newLogsCache };
                      }

                      const { data, error } = await urqlGql.getAuditLogs({
                        accountId: ctx.selectedOrgId,
                        queryMetadata,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return {
                        data: data?.admin_?.auditLogs,
                        queryMetadata,
                      };
                    },
                    onDoneTarget: 'loaded',
                    onDoneAssignContext({ ctx, data }) {
                      if (data && data.data !== undefined) {
                        const { queryMetadata } = data;
                        ctx.logs = data.data;
                        ctx.logsCache[
                          `${ctx.selectedOrgId}_${queryMetadata.skipPages}_${queryMetadata.pageSize}`
                        ] = data.data;
                      }
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading logs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {},
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            childOrgs: {
              id: 'childOrgs',
              initial: 'idle',
              on: {
                'manager.childOrgs.loadChildOrgs': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  id: 'childOrgsLoading',
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<GetChildAccountsQuery['admin_'] | undefined> => {
                      if (ctx.selectedOrgId === undefined) {
                        throw Error('Missing organization to load child orgs!');
                      }

                      const { data, error } = await urqlGql.getChildAccounts({
                        accountId: ctx.selectedOrgId,
                      });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }

                      return data?.admin_;
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (data !== undefined) {
                        ctx.childOrgs = {
                          rows: data.accounts?.rows,
                          count: data.accounts?.count,
                          currentOrg: data.accountById,
                        };
                      }
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading child orgs: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  on: {
                    'manager.selectOrgId': {
                      actions: assign((ctx) => {
                        ctx.childOrgs = {
                          rows: [],
                          count: 0,
                          currentOrg: undefined,
                        };
                        return ctx;
                      }),
                      target: 'loading',
                    },
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            currentEnvironment: {
              id: 'currentEnvironment',
              initial: 'notSelected',
              on: {
                'manager.selectEnvironment': {
                  actions: assign((ctx, { id }) => {
                    ctx.selectedEnvironment = id;
                    return ctx;
                  }),
                  target: '.selected',
                },
              },
              states: {
                notSelected: {},
                selected: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (!ctx.selectedEnvironment) return;

                      const { data, error } =
                        await urqlGql.getProjectEnvironmentConfigById({
                          id: ctx.selectedEnvironment,
                        });

                      if (!data?.admin_?.projectEnvironmentConfigById) {
                        throw new NotFoundError('Environment not found');
                      }

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      if (error instanceof NotFoundError) {
                        ctx.errors = [error];
                      }
                    },
                  }),
                },
              },
            },
            userDetails: {
              id: 'usersDetails',
              initial: 'idle',
              on: {
                'manager.loadUserDetails': '.loading',
                'manager.addNewUser': '.addNewUser.loadingPrerequisites',
                'manager.userDetails.setStateToIdle': '.idle',
                'manager.autoSaveUserDetails': '.autoSaveUserDetails',
                'manager.userDetails.addProject': {
                  actions: handleAddProjectAccess,
                  cond: canManageEventOrg,
                },
                'manager.userDetails.addTeam': {
                  target: '.addTeamToOrg',
                  cond: canManageEventOrg,
                },
                'manager.userDetails.updateProjectProductRole': {
                  actions: updateProjectProductRole,
                  cond: canManageEventOrg,
                },
                'manager.userDetails.updateProjectMetabaseAccess': {
                  actions: updateProjectMetabaseAccess,
                  cond: (ctx) => !!ctx.userInfo.canManageAccess,
                },
                'manager.userDetails.removeProjectWithRoles': {
                  actions: removeProjectWithRoles,
                  cond: canManageEventOrg,
                },
                'manager.userDetails.updateManagerRole': {
                  actions: updateManagerRole,
                  cond: canManageEventOrg,
                },
                'manager.userDetails.removeTeam': {
                  actions: removeTeam,
                  cond: canManageEventOrg,
                },
                'manager.userDetails.updateUserSettings': {
                  actions: updateUserSettings,
                },
                'manager.userDetails.discardUpdates': {
                  actions: immerAssign((ctx, event) => {
                    if (ctx.userDetails[event.userId]) {
                      ctx.userDetails[event.userId].state =
                        ctx.userDetails[event.userId].initialState;
                      ctx.userDetails[event.userId].userDetailUpdates =
                        userDetailUpdateInitialState;
                    }
                    return ctx;
                  }),
                },
                'manager.userDetails.saveUpdates': '.saveUpdates',
                'manager.userDetails.inviteUser': {
                  target: '.addNewUser.invitingUser',
                  cond: (ctx, event) => !!ctx.userInfo.canManageAccess,
                },
                'manager.userDetails.deleteUser.openConfirmation': {
                  target: '.deleteUser.confirmUserDeletion',
                  cond: (ctx, event) => !!ctx.userInfo.canManageAccess,
                },
                'manager.userDetails.deleteUser.confirmed': {
                  target: '.deleteUser.deleting',
                  cond: (ctx, event) => !!ctx.userInfo.canManageAccess,
                },
                'manager.userDetails.toggleOrgCollapseState': {
                  actions: immerAssign((ctx, event) => {
                    if (
                      ctx.selectedUser &&
                      event.type ===
                        'manager.userDetails.toggleOrgCollapseState'
                    ) {
                      const currentUserDetails =
                        ctx.userDetails[ctx.selectedUser];

                      if (currentUserDetails) {
                        const orgIndexToUpdate = (
                          currentUserDetails.state?.orgs || []
                        ).findIndex((org) => org.id === event.orgId);

                        if (
                          currentUserDetails.state &&
                          currentUserDetails.initialState &&
                          orgIndexToUpdate >= 0
                        ) {
                          currentUserDetails.state.orgs[
                            orgIndexToUpdate
                          ].isCollapsed = event.collapse;

                          currentUserDetails.initialState.orgs[
                            orgIndexToUpdate
                          ].isCollapsed = event.collapse;
                        }
                      }
                    }
                  }),
                },
                'manager.userDetails.toggleAllOrgCollapseState': {
                  actions: immerAssign((ctx, event) => {
                    if (
                      ctx.selectedUser &&
                      event.type ===
                        'manager.userDetails.toggleAllOrgCollapseState'
                    ) {
                      ctx.userDetails[ctx.selectedUser].state?.orgs.forEach(
                        (org) => (org.isCollapsed = event.collapse)
                      );

                      ctx.userDetails[
                        ctx.selectedUser
                      ].initialState?.orgs.forEach(
                        (org) => (org.isCollapsed = event.collapse)
                      );
                    }
                  }),
                },
              },
              states: {
                idle: {},
                loading: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<{
                      userDetails: UserDetailsType;
                      userDetailUpdates: UserDetailUpdates;
                    } | null> => {
                      if (event.type !== 'manager.loadUserDetails') {
                        return null;
                      }

                      return Promise.all([
                        urqlGql.getAccountDetails({ id: event.orgId }),
                        urqlGql.getUserDetails({
                          id: event.userId,
                        }),
                        urqlGql.getUserAuthMethods({ id: event.userId }),
                      ]).then(
                        async ([
                          { data: orgDetails, error: orgDetailsError },
                          { data: userAccountDetails, error: userDetailsError },
                          {
                            data: userAuthMethods,
                            error: userAuthMethodsError,
                          },
                        ]) => {
                          if (
                            orgDetailsError ||
                            userDetailsError ||
                            userAuthMethodsError ||
                            !orgDetails
                          ) {
                            throw new Error(
                              `*OrgDetailsError - ${orgDetailsError?.message}. *UserDetailsError - ${userDetailsError?.message}. *UserAuthMethodsError - ${userAuthMethodsError?.message}`
                            );
                          }
                          const allOrgs = handleOrgsDataProcessing(orgDetails);

                          if (
                            allOrgs &&
                            userAccountDetails?.tenancy_users_by_pk &&
                            userAccountDetails.tenancy_users_by_pk.user_accounts
                              .length > 0
                          ) {
                            // PSTUDIO-3111: per-org canManageAccess (backend is the
                            // source of truth). Each org section is gated by ITS OWN
                            // canManageAccess so a role inherited via a team in a child
                            // org grants edit rights there even with no parent-org role.
                            // E2E deferred — requires a backend fixture (child org +
                            // team granting Org Admin + member with parent-org role
                            // removed) not available in the manager Playwright harness,
                            // and depends on backend per-org canManageAccess (see
                            // PSTUDIO-3101). Covered by unit tests + manual QA.
                            // The route org's flag is already on the
                            // getAccountDetails response — seed it instead of
                            // re-querying.
                            const canManageAccessByOrgId =
                              await fetchCanManageAccessByOrgId(
                                allOrgs,
                                fetchOrganizationMetadata,
                                {
                                  [event.orgId]:
                                    !!orgDetails?.admin_?.organizationMetadata
                                      ?.canManageAccess,
                                }
                              );
                            const {
                              accountRoles,
                              availableAccountRoles,
                              roleIdsToRemove,
                            } = processAccountRoles(
                              userAccountDetails.tenancy_users_by_pk
                                ?.user_accounts[0]._user_account_roles,
                              event.loggedInUserHighestRole,
                              orgDetails?.admin_?.accountById?.roles
                            ) as {
                              accountRoles: AccountRole[];
                              availableAccountRoles: AccountRole[];
                              roleIdsToRemove: string[];
                            };

                            const assignedTeamIds =
                              userAccountDetails.tenancy_users_by_pk?._user_teams.map(
                                (team) => team.team.id
                              );
                            const userPrimaryAccountId =
                              userAccountDetails.tenancy_users_by_pk
                                ?.user_accounts[0].isPrimaryAccount &&
                              userAccountDetails.tenancy_users_by_pk
                                ?.user_accounts[0].accountId;

                            const projectRolesUpdate: SetUserAccountProjectsRolesMutationVariables['projectRolesUpdate'] =
                              [];
                            const userOrgs: ShortOrgType[] = allOrgs.map(
                              (org, currentOrgIndex) => {
                                const {
                                  assignedProjects,
                                  orgRowSpan,
                                  assignedProjectIds,
                                } =
                                  userAccountDetails.tenancy_users_by_pk!.user_accounts[0].user_account_projects!.reduce(
                                    (
                                      calculatedDetails: OrgRowSpanCalculatorType,
                                      currentProject: AccountProject
                                    ) => {
                                      if (
                                        org.id ===
                                        currentProject.project.accountId
                                      ) {
                                        const assignedProjectRoles =
                                          currentProject._account_project_roles
                                            .filter((role) => !role.teams)
                                            .map((role) => role.role.id);

                                        currentProject.project.project_product_settings.forEach(
                                          (product) => {
                                            const availableProductRoles =
                                              orgDetails?.admin_?.accountById?.roles?.rows?.filter(
                                                (role) =>
                                                  role.productId ===
                                                    product.product.id &&
                                                  !assignedProjectRoles.includes(
                                                    role.id!
                                                  )
                                              );
                                            if (
                                              !currentProject.availableProjectRoles
                                            ) {
                                              currentProject.availableProjectRoles =
                                                [];
                                            }

                                            if (availableProductRoles) {
                                              currentProject.availableProjectRoles.push(
                                                ...availableProductRoles
                                              );
                                            }
                                          }
                                        );

                                        const userAccountProjectRoles =
                                          currentProject._account_project_roles.reduce(
                                            (accumulatedRoles, currentRole) => {
                                              const sameProductRoleIdx =
                                                accumulatedRoles?.findIndex(
                                                  (roles) =>
                                                    roles.role.productId ===
                                                    currentRole.role.productId
                                                );
                                              const sameProductRole =
                                                accumulatedRoles[
                                                  sameProductRoleIdx
                                                ];

                                              if (
                                                (currentRole.teams || [])
                                                  ?.length === 0 &&
                                                sameProductRoleIdx >= 0
                                              ) {
                                                const existingProjectRoleUpdateIndex =
                                                  projectRolesUpdate.findIndex(
                                                    (project) =>
                                                      project.id ===
                                                      currentProject.project.id
                                                  );
                                                // If role is highest possible role for product, then keep that role assigned and remove other low level roles.
                                                if (
                                                  currentRole.role.name ===
                                                    AgentAssistRoleNames.AGENT_ASSIST_ADMIN ||
                                                  currentRole.role.name ===
                                                    AnalyticsRoleNames.ANALYTICS_ADMIN
                                                ) {
                                                  // make this role available with availableProjectRoles
                                                  currentProject.availableProjectRoles?.push(
                                                    {
                                                      id: sameProductRole.role
                                                        .id,
                                                      name: sameProductRole.role
                                                        .name,
                                                      productId:
                                                        sameProductRole.role
                                                          .productId,
                                                    }
                                                  );
                                                  if (
                                                    existingProjectRoleUpdateIndex >=
                                                    0
                                                  ) {
                                                    projectRolesUpdate[
                                                      existingProjectRoleUpdateIndex
                                                    ].rolesToRemove?.push(
                                                      sameProductRole.role.id
                                                    );
                                                  } else {
                                                    projectRolesUpdate.push({
                                                      id: currentProject.project
                                                        .id,
                                                      rolesToRemove: [
                                                        sameProductRole.role.id,
                                                      ],
                                                      rolesToAdd: [],
                                                    });
                                                  }
                                                  accumulatedRoles[
                                                    sameProductRoleIdx
                                                  ] = currentRole;
                                                } else {
                                                  currentProject.availableProjectRoles?.push(
                                                    {
                                                      id: currentRole.role.id,
                                                      name: currentRole.role
                                                        .name,
                                                      productId:
                                                        currentRole.role
                                                          .productId,
                                                      product: {
                                                        id: currentRole.role
                                                          .product?.id,
                                                        name: currentRole.role
                                                          .product
                                                          ?.name as ProductName,
                                                        displayName:
                                                          currentRole.role
                                                            .product
                                                            ?.displayName,
                                                      },
                                                    }
                                                  );
                                                  if (
                                                    existingProjectRoleUpdateIndex >=
                                                    0
                                                  ) {
                                                    projectRolesUpdate[
                                                      existingProjectRoleUpdateIndex
                                                    ].rolesToRemove?.push(
                                                      currentRole.role.id
                                                    );
                                                  } else {
                                                    projectRolesUpdate.push({
                                                      id: currentProject.project
                                                        .id,
                                                      rolesToRemove: [
                                                        currentRole.role.id,
                                                      ],
                                                      rolesToAdd: [],
                                                    });
                                                  }
                                                }

                                                return accumulatedRoles;
                                              } else {
                                                accumulatedRoles.push(
                                                  currentRole
                                                );
                                                return accumulatedRoles;
                                              }
                                            },
                                            [] as AccountProject['_account_project_roles']
                                          );

                                        currentProject._account_project_roles =
                                          sortBy(
                                            userAccountProjectRoles,
                                            'teams'
                                          );

                                        currentProject.project.project_product_settings.sort(
                                          productsSortFunction
                                        );

                                        calculatedDetails.assignedProjects?.push(
                                          currentProject
                                        );
                                        calculatedDetails.assignedProjectIds.push(
                                          currentProject.project.id
                                        );

                                        // Add 1 row span per project
                                        calculatedDetails.orgRowSpan =
                                          calculatedDetails.orgRowSpan + 1;

                                        // Add row span for project products.
                                        calculatedDetails.orgRowSpan =
                                          calculatedDetails.orgRowSpan +
                                          Math.max(
                                            currentProject.project
                                              .project_product_settings.length -
                                              1,
                                            0
                                          );
                                      }
                                      return calculatedDetails;
                                    },
                                    {
                                      assignedProjects: [],
                                      orgRowSpan: 0,
                                      assignedProjectIds: [],
                                    } as OrgRowSpanCalculatorType
                                  );

                                const orgCanManageAccess =
                                  canManageAccessByOrgId[org.id!] ?? false;
                                const showAddProjectButton = orgCanManageAccess;
                                const availableProjects =
                                  org.projects?.rows?.filter(
                                    (project) =>
                                      !assignedProjectIds.includes(project.id)
                                  ) || [];
                                const availableTeams = org.teams?.rows?.filter(
                                  (team) => {
                                    if (
                                      !event.loggedInUserHighestRole.roleLevel
                                    ) {
                                      return false;
                                    }

                                    const isNotAssigned =
                                      !assignedTeamIds.includes(team.id!);

                                    const highestAssignedRole =
                                      team.assignedRoles
                                        ?.map((role) => ({
                                          ...getRoleLevelForAccountRoles(
                                            role.name || ''
                                          ),
                                          ...role,
                                        }))
                                        .sort(
                                          (a, b) => a.roleLevel - b.roleLevel
                                        )
                                        .shift();

                                    if (
                                      !highestAssignedRole ||
                                      !highestAssignedRole.roleLevel
                                    ) {
                                      return isNotAssigned;
                                    }

                                    return (
                                      highestAssignedRole.roleLevel >=
                                        event.loggedInUserHighestRole
                                          .roleLevel && isNotAssigned
                                    );
                                  }
                                );

                                let _orgRowSpan = orgRowSpan;
                                if (
                                  (showAddProjectButton &&
                                    (availableProjects.length > 0 ||
                                      assignedProjects?.length === 0)) ||
                                  (!showAddProjectButton &&
                                    assignedProjects?.length === 0)
                                ) {
                                  // Add extra row only if we are showing add-project button OR no projects available message.
                                  _orgRowSpan = orgRowSpan + 1;
                                }
                                // If we have org more than 3, then collapse all orgs by default.
                                let isCollapsed = allOrgs.length > 3;
                                // If we have old local state for user, make sure to bring collapsed state from local state for better UX.
                                if (
                                  ctx.userDetails[event.userId]?.state?.orgs[
                                    currentOrgIndex
                                  ]
                                ) {
                                  isCollapsed =
                                    !!ctx.userDetails[event.userId]?.state
                                      ?.orgs[currentOrgIndex]?.isCollapsed;
                                }
                                return {
                                  showAddProjectButton,
                                  canManageAccess: orgCanManageAccess,
                                  name: org.name,
                                  id: org.id,
                                  parentAccount: org.parentAccount,
                                  accountRoles: sortBy(accountRoles, 'teams'),
                                  availableAccountRoles,
                                  assignedTeams:
                                    userAccountDetails.tenancy_users_by_pk?._user_teams.filter(
                                      (team) =>
                                        team.team.team_accounts[0].accountId ===
                                        org.id
                                    ),
                                  assignedProjects: orderBy(
                                    assignedProjects,
                                    (assignedProject) =>
                                      assignedProject.project.name.toLowerCase(),
                                    'asc'
                                  ),
                                  orgRowSpan: _orgRowSpan,
                                  availableTeams,
                                  availableProjects,
                                  isPrimaryAccount:
                                    userPrimaryAccountId === org.id,
                                  roles: orgDetails?.admin_?.accountById?.roles,
                                  isCollapsed,
                                  allParentIds: org.allParentIds,
                                };
                              }
                            );

                            const userDetails: UserDetailsType = {
                              firstName:
                                userAccountDetails.tenancy_users_by_pk
                                  .firstName,
                              lastName:
                                userAccountDetails.tenancy_users_by_pk.lastName,
                              id: userAccountDetails.tenancy_users_by_pk.id,
                              userName:
                                userAccountDetails.tenancy_users_by_pk.userName,
                              picture:
                                userAccountDetails.tenancy_users_by_pk.picture,
                              orgs: userOrgs,
                              canManageAccess:
                                !!orgDetails?.admin_?.organizationMetadata
                                  ?.canManageAccess,
                              settings:
                                userAccountDetails.tenancy_users_by_pk
                                  .settings || {},
                              files:
                                userAccountDetails.tenancy_users_by_pk.files,
                              optionalConsentStatus:
                                userAccountDetails.tenancy_users_by_pk
                                  .optionalConsentStatus,
                              requiredConsentStatus:
                                userAccountDetails.tenancy_users_by_pk
                                  .requiredConsentStatus,
                              userAuthMethods:
                                userAuthMethods?.admin_?.userById
                                  ?.listOfAuthMethods || [],
                            };

                            const _userDetails =
                              userAccountDetails.tenancy_users_by_pk?._user_teams.reduce(
                                (acc, team) => {
                                  return processTeamInheritedDetails({
                                    teamDetails: team.team,
                                    selectedUserDetails: acc,
                                  });
                                },
                                userDetails as UserDetailsType
                              );

                            return {
                              userDetails: _userDetails,
                              userDetailUpdates: {
                                projectRolesUpdate,
                                hasPendingUpdates: false,
                                roleIdsToAdd: [],
                                roleIdsToRemove,
                                teamIdsToAdd: [],
                                teamIdsToRemove: [],
                                projectIdsToAdd: [],
                                projectIdsToRemove: [],
                              },
                            };
                          }

                          return null;
                        }
                      );
                    },
                    onDoneAssignContext({ ctx, data }) {
                      // Update context only if incoming data has differences.
                      if (!data) return;

                      if (!ctx.userDetails[data.userDetails.id]) {
                        ctx.userDetails[data.userDetails.id] = {
                          state: data.userDetails,
                          userDetailUpdates: data.userDetailUpdates,
                          initialState: data.userDetails,
                        };
                      } else if (
                        !isEqual(
                          ctx.userDetails[data.userDetails.id].state,
                          data.userDetails
                        )
                      ) {
                        ctx.userDetails[data.userDetails.id].state =
                          data.userDetails;
                      }
                      return ctx;
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading users: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {
                  entry: (ctx) => {
                    const details = ctx.userDetails[ctx.selectedUser!];
                    // PSTUDIO-3111: the edit guards are per-org, so the
                    // consolidating autosave must also run when the route
                    // org is not manageable but at least one pending
                    // project-role update targets a manageable org —
                    // otherwise a child-org edit is accepted and silently
                    // never persisted.
                    const canPersistPendingUpdates =
                      !!details?.state?.canManageAccess ||
                      filterProjectRolesUpdateByOrgAccess(
                        details?.state?.orgs,
                        details?.userDetailUpdates.projectRolesUpdate
                      ).length > 0;
                    if (
                      details &&
                      hasPendingUpdates(details) &&
                      canPersistPendingUpdates
                    ) {
                      sendManagerEvent({
                        type: 'manager.autoSaveUserDetails',
                      });
                    } else {
                      sendManagerEvent({
                        type: 'manager.userDetails.setStateToIdle',
                      });
                    }
                  },
                },
                autoSaveUserDetails: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (!ctx.selectedUser) return;

                      // PSTUDIO-3111: consolidation entries can span orgs the
                      // logged-in user cannot manage (they are generated
                      // without going through the per-org guards) — persist
                      // only the manageable ones, fail-closed for the rest.
                      const projectRolesUpdate =
                        filterProjectRolesUpdateByOrgAccess(
                          ctx.userDetails[ctx.selectedUser!]?.state?.orgs,
                          ctx.userDetails[ctx.selectedUser!]?.userDetailUpdates
                            .projectRolesUpdate
                        );

                      if (projectRolesUpdate.length) {
                        const { data, error } =
                          await urqlGql.setUserAccountProjectsRoles({
                            userId: ctx.selectedUser,
                            projectRolesUpdate,
                            isConsolidatingDetails: true,
                          });

                        if (haveErrors(data)) {
                          throw new Error(data.errors?.[0]?.message);
                        }

                        if (error) {
                          throw new Error(error.message);
                        }
                      }

                      if (
                        (
                          ctx.userDetails[ctx.selectedUser!]?.userDetailUpdates
                            .roleIdsToRemove || []
                        ).length &&
                        // the role-removal mutation targets the route org —
                        // keep it gated on that org's flag now that the
                        // autosave entry can fire without it
                        ctx.userDetails[ctx.selectedUser!]?.state
                          ?.canManageAccess
                      ) {
                        await handleUpdateUserDetails({
                          userId: ctx.selectedUser,
                          // @todo - do a better job of getting the orgId
                          orgId:
                            ctx.userDetails[ctx.selectedUser!].state?.orgs.find(
                              (org) => org.isPrimaryAccount
                            )?.id || '',
                          userDetails: ctx.userDetails[ctx.selectedUser!],
                          isConsolidatingDetails: true,
                        });
                      }
                    },
                    onDoneTarget: 'idle',
                    onDoneAssignContext({ ctx, data }) {
                      if (!ctx.selectedUser) return;
                      ctx.userDetails[
                        ctx.selectedUser
                      ]!.userDetailUpdates.projectRolesUpdate = [];
                      ctx.userDetails[
                        ctx.selectedUser
                      ]!.userDetailUpdates.hasPendingUpdates = false;
                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating user details: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                addTeamToOrg: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<{
                      teamDetails:
                        | GetTeamProjectsDetailsQuery['tenancy_teams_by_pk']
                        | undefined;
                      orgId: string;
                      team: NonNullable<AccountTeams>[number];
                    } | null> => {
                      if (
                        event.type !== 'manager.userDetails.addTeam' ||
                        !event.team.id
                      )
                        return null;

                      const { data, error } =
                        await urqlGql.getTeamProjectsDetails({
                          teamId: event.team.id,
                        });

                      if (haveErrors(data)) {
                        throw new Error(data.errors?.[0]?.message);
                      }

                      if (error) {
                        throw new Error(error.message);
                      }
                      return {
                        teamDetails: data?.tenancy_teams_by_pk,
                        orgId: event.orgId,
                        team: event.team,
                      };
                    },
                    onDoneTarget: 'idle',
                    onDoneAssignContext({ ctx, data }) {
                      if (!data || !ctx.selectedUser) return;

                      const selectedUserDetails =
                        ctx.userDetails[ctx.selectedUser];
                      if (!selectedUserDetails || !data.teamDetails) return;
                      const orgToUpdate = selectedUserDetails.state?.orgs.find(
                        (org) => org.id === data.orgId
                      );

                      if (!orgToUpdate) return;

                      // Remove the team from available teams
                      orgToUpdate.availableTeams =
                        orgToUpdate.availableTeams?.filter(
                          (availableTeam) => availableTeam.id !== data.team.id
                        );

                      orgToUpdate.assignedTeams?.push({
                        team: data.teamDetails,
                      });

                      if (selectedUserDetails) {
                        processTeamInheritedDetails({
                          teamDetails: data.teamDetails,
                          selectedUserDetails: selectedUserDetails.state!,
                        });
                      }

                      if (!selectedUserDetails.userDetailUpdates.teamIdsToAdd) {
                        selectedUserDetails.userDetailUpdates.teamIdsToAdd = [];
                      }

                      if (
                        !(
                          selectedUserDetails.userDetailUpdates
                            .teamIdsToRemove || []
                        ).includes(data.teamDetails.id)
                      ) {
                        selectedUserDetails.userDetailUpdates.teamIdsToAdd.push(
                          data.teamDetails.id
                        );
                      } else {
                        selectedUserDetails.userDetailUpdates.teamIdsToRemove =
                          (
                            selectedUserDetails.userDetailUpdates
                              .teamIdsToRemove || []
                          ).filter((teamId) => teamId !== data.teamDetails?.id);
                      }

                      selectedUserDetails.userDetailUpdates.hasPendingUpdates =
                        hasPendingUpdates(selectedUserDetails);
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Failed to add team to org, Please try again`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                addNewUser: {
                  states: {
                    loadingPrerequisites: {
                      invoke: createInvokablePromise({
                        src: async (
                          ctx,
                          event
                        ): Promise<{
                          userDetails: UserDetailsType;
                        } | null> => {
                          if (event.type !== 'manager.addNewUser') {
                            return null;
                          }

                          return urqlGql
                            .getAccountDetails({ id: event.orgId! })
                            .then(async (orgDetails) => {
                              if (!orgDetails.data) {
                                throw new Error(
                                  `*OrgDetailsError - ${orgDetails.error?.message}.`
                                );
                              }
                              const allOrgs = handleOrgsDataProcessing(
                                orgDetails.data
                              );

                              const canManageAccess =
                                !!orgDetails?.data.admin_?.organizationMetadata
                                  ?.canManageAccess;

                              const canManageAccessByOrgId =
                                await fetchCanManageAccessByOrgId(
                                  allOrgs,
                                  fetchOrganizationMetadata,
                                  { [event.orgId!]: canManageAccess }
                                );
                              const userDetails: UserDetailsType = {
                                firstName: '',
                                lastName: '',
                                id: '',
                                userName: '',
                                picture: '',
                                orgs:
                                  allOrgs?.map((org) => {
                                    const orgCanManageAccess =
                                      canManageAccessByOrgId[org.id!] ?? false;
                                    return {
                                      ...org,
                                      isPrimaryAccount: org.id === event.orgId,
                                      canManageAccess: orgCanManageAccess,
                                      showAddProjectButton: orgCanManageAccess,
                                      assignedProjects: [],
                                      availableProjects: org.projects?.rows,
                                      assignedTeams: [],
                                      availableTeams: org.teams?.rows,
                                      accountRoles: [],
                                      availableAccountRoles:
                                        processAccountRoles(
                                          [],
                                          event.loggedInUserHighestRole,
                                          orgDetails.data?.admin_?.accountById
                                            ?.roles
                                        )
                                          .availableAccountRoles as AccountRole[],
                                      roles:
                                        orgDetails.data?.admin_?.accountById
                                          ?.roles,
                                      isCollapsed: allOrgs.length > 3,
                                    };
                                  }) || [],
                                canManageAccess,
                                files: [],
                                optionalConsentStatus: undefined,
                                requiredConsentStatus: undefined,
                                userAuthMethods: [],
                              };

                              return { userDetails };
                            });
                        },
                        onDoneAssignContext({ ctx, data }) {
                          // Update context only if incoming data has differences.
                          if (!data) return;
                          ctx.userDetails[NEW_USER_TEMP_ID] = {
                            state: data.userDetails,
                            userDetailUpdates: userDetailUpdateInitialState,
                            initialState: data.userDetails,
                          };
                          ctx.selectedUser = NEW_USER_TEMP_ID;
                          return ctx;
                        },
                        onDoneTarget: '#usersDetails.idle',
                        onErrorTarget: '#usersDetails.error',
                        onErrorActions: [
                          {
                            type: 'sendUserMessage',
                            exec(ctx, event) {
                              sendUserMessage({
                                type: 'error',
                                text: `Error adding new user: ${event.data.message}`,
                                autoClose: 3000,
                              });
                            },
                          },
                        ],
                      }),
                    },
                    invitingUser: {
                      entry: immerAssign((ctx, event) => {
                        if (event.type !== 'manager.userDetails.inviteUser')
                          return;

                        if (ctx.userDetails[NEW_USER_TEMP_ID].state) {
                          ctx.userDetails[NEW_USER_TEMP_ID].state.firstName =
                            event.form.firstName;
                          ctx.userDetails[NEW_USER_TEMP_ID].state.lastName =
                            event.form.lastName;
                          ctx.userDetails[NEW_USER_TEMP_ID].state.userName =
                            event.form.email;
                          ctx.userDetails[NEW_USER_TEMP_ID].state.settings = {
                            ...(ctx.userDetails[NEW_USER_TEMP_ID].state
                              .settings || {}),
                            jobTitle: event.form.jobTitle,
                          };
                        }
                      }),
                      invoke: createInvokablePromise({
                        src: async (
                          ctx,
                          event
                        ): Promise<
                          | {
                              userId: string;
                              createdAt: NonNullable<
                                NonNullable<
                                  AddUserToAccountMutation['admin_']
                                >['addUserToAccount']
                              >['createdAt'];
                              lastActiveAt: NonNullable<
                                NonNullable<
                                  AddUserToAccountMutation['admin_']
                                >['addUserToAccount']
                              >['lastActiveAt'];
                              allRoleNamesToAdd: string[];
                            }
                          | undefined
                        > => {
                          if (event.type !== 'manager.userDetails.inviteUser')
                            return;
                          const { orgId, form } = event;

                          try {
                            const {
                              data: newUserDetails,
                              error: newUserDetailsError,
                            } = await urqlGql.addUserToAccount({
                              accountId: orgId!,
                              userName: form.email,
                              firstName: form.firstName,
                              lastName: form.lastName,
                            });

                            if (newUserDetailsError) {
                              throw new Error(
                                `Error adding new user: ${newUserDetailsError.message}`
                              );
                            }

                            if (newUserDetails?.admin_?.addUserToAccount?.id) {
                              const { userId, allRoleNamesToAdd } =
                                await handleUpdateUserDetails({
                                  userId:
                                    newUserDetails?.admin_?.addUserToAccount
                                      ?.id,
                                  orgId: event.orgId!,
                                  userDetails:
                                    ctx.userDetails[NEW_USER_TEMP_ID],
                                });

                              return {
                                userId,
                                createdAt:
                                  newUserDetails?.admin_?.addUserToAccount
                                    ?.createdAt,
                                lastActiveAt:
                                  newUserDetails?.admin_?.addUserToAccount
                                    ?.lastActiveAt,
                                allRoleNamesToAdd,
                              };
                            }
                            return undefined;
                          } catch (error) {
                            throw new Error(
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-expect-error
                              error?.message || 'Error adding user'
                            );
                          }
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (!data) return;

                          sendUserMessage({
                            type: 'success',
                            text: 'Sent invite successfully',
                            autoClose: 3000,
                          });

                          ctx.userDetails[NEW_USER_TEMP_ID].state!.id =
                            data.userId;

                          ctx.userDetails[data.userId] = {
                            state: ctx.userDetails[NEW_USER_TEMP_ID].state,
                            userDetailUpdates: userDetailUpdateInitialState,
                            initialState:
                              ctx.userDetails[NEW_USER_TEMP_ID].state,
                          };

                          ctx.users?.push({
                            id: ctx.userDetails[data.userId].state!.id,
                            firstName:
                              ctx.userDetails[data.userId].state!.firstName,
                            lastName:
                              ctx.userDetails[data.userId].state!.lastName,
                            userName:
                              ctx.userDetails[data.userId].state!.userName,
                            createdAt: data.createdAt,
                            lastActiveAt: data.lastActiveAt,
                            status: UserStatus.Invited,
                          });

                          ctx.users = sortBy(ctx.users, 'firstName');
                          ctx.selectedUser = data.userId;
                          return ctx;
                        },
                        onDoneActions: [
                          (ctx, event) => {
                            if (
                              analytics &&
                              typeof analytics.has_opted_in_capturing ===
                                'function' &&
                              analytics.has_opted_in_capturing() &&
                              typeof analytics.capture === 'function' &&
                              event.data
                            ) {
                              const { allRoleNamesToAdd } = event.data;

                              allRoleNamesToAdd.forEach((roleName: string) => {
                                analytics.capture('Role assigned', {
                                  role_assigned: roleName,
                                });
                              });
                            }
                          },
                        ],
                        onDoneTarget: 'invited',
                        onErrorTarget: 'error',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onErrorActions: [
                          {
                            type: 'sendUserMessage',
                            exec(ctx, event) {
                              sendUserMessage({
                                type: 'error',
                                text: `Error adding new user: ${event.data.message}`,
                                autoClose: 3000,
                              });
                            },
                          },
                        ],
                      }),
                    },
                    invited: {
                      after: {
                        1000: {
                          // Once user is invited, set state to idle.
                          target: '#usersDetails.idle',
                          actions: immerAssign((ctx, event) => {
                            ctx.userDetails[NEW_USER_TEMP_ID] = {
                              state: null,
                              userDetailUpdates: userDetailUpdateInitialState,
                              initialState: null,
                            };
                          }),
                        },
                      },
                    },
                    error: {
                      tags: [ErrorTags.xstate],
                      exit: assign((ctx, _event) => {
                        return {
                          ...ctx,
                          errors: [],
                        };
                      }),
                    },
                  },
                },
                deleteUser: {
                  states: {
                    confirmUserDeletion: {},
                    deleting: {
                      invoke: createInvokablePromise({
                        src: async (
                          ctx,
                          event
                        ): Promise<
                          | {
                              deletedUserId: string;
                            }
                          | undefined
                        > => {
                          if (
                            event.type !==
                            'manager.userDetails.deleteUser.confirmed'
                          ) {
                            return;
                          }

                          try {
                            const {
                              data: deletedUserDetails,
                              error: deletedUserError,
                            } = await urqlGql.removeUserFromAccount({
                              accountId: event.orgId!,
                              userIds: [event.userId!],
                            });

                            if (deletedUserError) {
                              sendUserMessage({
                                type: 'error',
                                text: `Error deleting user: ${deletedUserError.message}`,
                                autoClose: 3000,
                              });
                              // These two are the only error toasts in this
                              // machine the invoke-level reporter does not
                              // reach: every other one sits in `onErrorActions`,
                              // while this path RETURNS instead of throwing.
                              reportManagerFailure(
                                deletedUserError,
                                'remove-user-from-account'
                              );

                              return;
                            }

                            if (
                              !deletedUserDetails?.admin_
                                ?.removeUsersFromAccount
                            )
                              return;

                            if (
                              deletedUserDetails?.admin_
                                ?.removeUsersFromAccount[0].errorDetails
                            ) {
                              sendUserMessage({
                                type: 'error',
                                text: `Error deleting user: ${deletedUserDetails?.admin_?.removeUsersFromAccount[0].errorDetails}`,
                                autoClose: 3000,
                              });
                              // A 200 carrying `errorDetails` — a failure the
                              // transport calls a success, so nothing but this
                              // line can see it.
                              reportManagerFailure(
                                new Error(
                                  String(
                                    deletedUserDetails.admin_
                                      .removeUsersFromAccount[0].errorDetails
                                  )
                                ),
                                'remove-user-from-account:error-details'
                              );

                              return;
                            }

                            if (
                              deletedUserDetails?.admin_
                                ?.removeUsersFromAccount[0].succeeded
                            ) {
                              return { deletedUserId: event.userId! };
                            }

                            return undefined;
                          } catch (error) {
                            throw new Error(
                              `Something went wrong deleting user - ${error}`
                            );
                          }
                        },
                        onDoneAssignContext({ ctx, data }) {
                          // Update context only if incoming data has differences.
                          if (!data) return;
                          ctx.users = ctx.users?.filter(
                            (user) => user.id !== data.deletedUserId
                          );
                          ctx.selectedUser = undefined;
                          return ctx;
                        },
                        onDoneTarget: 'deleted',
                        onErrorTarget: 'error',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onErrorActions: [
                          {
                            type: 'sendUserMessage',
                            exec(ctx, event) {
                              sendUserMessage({
                                type: 'error',
                                text: `Error deleting selected user: ${event.data.message}`,
                                autoClose: 3000,
                              });
                            },
                          },
                        ],
                      }),
                    },
                    deleted: {},
                    error: {
                      tags: [ErrorTags.xstate],
                      exit: assign((ctx, _event) => {
                        return {
                          ...ctx,
                          errors: [],
                        };
                      }),
                    },
                  },
                },
                saveUpdates: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | {
                          userId: string;
                          form: {
                            firstName: string;
                            lastName: string;
                            optionalConsentStatus?: ConsentStatusEnum;
                            profilePhoto?: string;
                            userIconId?: string;
                            jobTitle?: string;
                            defaultLanguage?: string;
                            defaultTimezone?: string;
                          };
                          allRoleNamesToAdd: string[];
                        }
                      | undefined
                    > => {
                      if (event.type !== 'manager.userDetails.saveUpdates')
                        return;
                      const { userId, orgId, form } = event;
                      const userDetails = ctx.userDetails[userId];
                      if (!userDetails || !orgId) return;

                      const { allRoleNamesToAdd } =
                        await handleUpdateUserDetails({
                          userId,
                          orgId,
                          userDetails: {
                            ...userDetails,
                            state: {
                              ...userDetails.state!,
                              settings: {
                                ...userDetails.state!.settings,
                                jobTitle: event.form.jobTitle,
                                defaultLanguage: event.form.defaultLanguage,
                                defaultTimezone: event.form.defaultTimezone,
                              },
                            },
                          },
                          firstName: form.firstName,
                          lastName: form.lastName,
                          optionalConsentStatus: form.optionalConsentStatus,
                          userIconId: form.userIconId,
                        });

                      return {
                        userId: event.userId,
                        form,
                        allRoleNamesToAdd,
                      };
                    },
                    onDoneTarget: 'userDetailsUpdated',
                    onDoneActions: [
                      (ctx, event) => {
                        if (
                          analytics &&
                          typeof analytics.has_opted_in_capturing ===
                            'function' &&
                          analytics.has_opted_in_capturing() &&
                          typeof analytics.capture === 'function' &&
                          event.data
                        ) {
                          const { allRoleNamesToAdd } = event.data;

                          allRoleNamesToAdd.forEach((roleName: string) => {
                            analytics.capture('Role assigned', {
                              role_assigned: roleName,
                            });
                          });
                        }
                      },
                      (ctx, event) => {
                        if (event.data?.userId === ctx.userInfo.id) {
                          sendManagerEvent({
                            type: 'manager.refetchUserSettings',
                            orgId: ctx.selectedOrgId,
                          });
                        }
                      },
                    ],
                    onDoneAssignContext({ ctx, data }) {
                      sendUserMessage({
                        type: 'success',
                        text: 'User details updated successfully',
                        autoClose: 3000,
                      });
                      if (!ctx.selectedUser) return;
                      // Update initial state to save state as a starting point.
                      ctx.userDetails[ctx.selectedUser].state!.firstName =
                        data!.form.firstName;
                      ctx.userDetails[ctx.selectedUser].state!.lastName =
                        data!.form.lastName;
                      ctx.userDetails[
                        ctx.selectedUser
                      ].state!.optionalConsentStatus =
                        data!.form.optionalConsentStatus;
                      if (data!.form.userIconId != null) {
                        ctx.userDetails[ctx.selectedUser].state!.files = data!
                          .form.profilePhoto
                          ? [{ url: data!.form.profilePhoto }]
                          : [];
                      }
                      ctx.userDetails[ctx.selectedUser].state!.settings = {
                        ...(ctx.userDetails[ctx.selectedUser].state!.settings ||
                          {}),
                        jobTitle: data!.form.jobTitle,
                        defaultLanguage: data!.form.defaultLanguage,
                        defaultTimezone: data!.form.defaultTimezone,
                      };

                      ctx.userDetails[ctx.selectedUser].initialState =
                        ctx.userDetails[ctx.selectedUser].state;
                      ctx.userDetails[ctx.selectedUser]!.userDetailUpdates =
                        userDetailUpdateInitialState;

                      const userIndex = ctx.users?.findIndex(
                        (user) => user.id === ctx.selectedUser
                      );

                      if (userIndex == null || userIndex === -1 || !ctx.users)
                        return ctx;

                      ctx.users[userIndex] = {
                        ...ctx.users[userIndex],
                        firstName:
                          ctx.userDetails[ctx.selectedUser].state!.firstName,
                        lastName:
                          ctx.userDetails[ctx.selectedUser].state!.lastName,
                      };

                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating user details: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                userDetailsUpdated: {
                  after: {
                    100: 'idle',
                  },
                },
                reloadCurrentUser: {
                  id: 'reloadCurrentUser',
                  entry: (
                    ctx,
                    event: ManagerEvents & { data: { userId: string } }
                  ) => {
                    if (event.data.userId === ctx.userInfo.id) {
                      sendUserEvent({ type: 'user.refetch' });
                    }
                  },
                  always: {
                    target: 'idle',
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            teamDetails: {
              id: 'teamDetails',
              initial: 'idle',
              on: {
                'manager.teamDetails.loadTeamDetails': '.loading',
                'manager.teamDetails.initiateNewTeam': '.initiateNewTeam',
                'manager.teamDetails.discardUpdates': {
                  actions: discardTeamChanges,
                },
                'manager.teamDetails.createNewTeam': '.createNewTeam',
                'manager.teamDetails.saveUpdates': '.saveUpdates',
                'manager.teamDetails.deleteTeam': '.deleteTeam',
                'manager.teamDetails.clearTeamState': {
                  actions: clearTeamState,
                },
                'manager.teamDetails.addUsersToTeam': {
                  actions: addUsersToTeam,
                },
                'manager.teamDetails.removeUserFromTeam': {
                  actions: removeUserFromTeam,
                },
                'manager.teamDetails.toggleAllOrgsCollapseState': {
                  actions: toggleTeamAllOrgsCollapseState,
                },
                'manager.teamDetails.toggleOrgCollapseState': {
                  actions: toggleTeamOrgCollapseState,
                },
                'manager.teamDetails.addProject': {
                  actions: handleAddProjectToTeam,
                },
                'manager.teamDetails.updateProjectProductRole': {
                  actions: updateTeamProjectProductRole,
                },
                'manager.teamDetails.removeProjectWithRoles': {
                  actions: removeTeamProjectWithRoles,
                },
                'manager.teamDetails.updateManagerRole': {
                  actions: updateTeamManagerRole,
                },
              },
              states: {
                idle: {},
                loading: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<{
                      teamDetails: TeamDetailsType;
                      availableUsers: NonNullable<
                        GetTeamDetailsQuery['tenancy_users']
                      >;
                      orgs: ShortOrgType[];
                      teamDetailUpdates: TeamDetailUpdates;
                      canManageAccess: boolean;
                    } | void> => {
                      if (
                        event.type !== 'manager.teamDetails.loadTeamDetails' ||
                        !event.teamId ||
                        !event.orgId
                      ) {
                        return;
                      }

                      const [
                        { data: teamDetails, error: teamDetailsError },
                        { data: accountDetails, error: accountDetailsError },
                      ] = await Promise.all([
                        urqlGql.getTeamDetails({
                          accountId: event.orgId,
                          teamId: event.teamId,
                        }),
                        urqlGql.getAccountDetails({ id: event.orgId }),
                      ]);

                      if (teamDetailsError) {
                        throw new Error(teamDetailsError.message);
                      }

                      if (accountDetailsError) {
                        throw new Error(accountDetailsError.message);
                      }

                      // we handle not found case on router page
                      if (
                        !teamDetails?.tenancy_teams_by_pk ||
                        !accountDetails?.admin_?.accountById
                      )
                        return;

                      const teamUserIds =
                        teamDetails.tenancy_teams_by_pk.usersFromCurrentOrg.map(
                          (user) => user.user.id
                        );

                      teamDetails.tenancy_teams_by_pk.usersFromCurrentOrg =
                        sortBy(
                          teamDetails.tenancy_teams_by_pk.usersFromCurrentOrg,
                          'user.firstName',
                          'user.lastName'
                        );
                      const availableUsers = sortBy(
                        teamDetails.tenancy_users.filter(
                          (user) => !teamUserIds.includes(user.id)
                        ),
                        'firstName',
                        'lastName'
                      );

                      const allOrgs = handleOrgsDataProcessing(accountDetails);

                      const canManageAccessByOrgId =
                        await fetchCanManageAccessByOrgId(
                          allOrgs,
                          fetchOrganizationMetadata,
                          {
                            [event.orgId]:
                              !!accountDetails?.admin_?.organizationMetadata
                                ?.canManageAccess,
                          }
                        );

                      const {
                        accountRoles,
                        availableAccountRoles,
                        roleIdsToRemove,
                      } = processAccountRoles(
                        teamDetails.tenancy_teams_by_pk.team_accounts[0]
                          ._team_account_roles,
                        event.loggedInUserHighestRole,
                        accountDetails?.admin_?.accountById?.roles
                      ) as {
                        accountRoles: AccountRole[];
                        availableAccountRoles: AccountRole[];
                        roleIdsToRemove: string[];
                      };

                      const teamPrimaryAccountId =
                        getTeamPrimaryAccountId(teamDetails);

                      const projectRolesUpdate: SetTeamAccountProjectRolesMutationVariables['projectRolesUpdate'] =
                        [];

                      const teamOrgs: ShortOrgType[] = allOrgs.map(
                        (org, currentOrgIndex) => {
                          const {
                            assignedProjects,
                            orgRowSpan,
                            assignedProjectIds,
                          } =
                            teamDetails.tenancy_teams_by_pk!.team_accounts[0].team_account_projects.reduce(
                              (
                                calculatedDetails: {
                                  assignedProjects: AccountProject[];
                                  orgRowSpan: number;
                                  assignedProjectIds: string[];
                                },
                                currentProject: AccountProject
                              ) => {
                                if (org.id !== currentProject.project.accountId)
                                  return calculatedDetails;

                                const assignedProjectRoleIds =
                                  currentProject._account_project_roles.map(
                                    (role) => role.role.id
                                  );

                                currentProject.availableProjectRoles =
                                  currentProject.availableProjectRoles || [];

                                currentProject.availableProjectRoles.push(
                                  ...getProjectAvailableRoles(
                                    currentProject.project
                                      .project_product_settings,
                                    assignedProjectRoleIds,
                                    accountDetails?.admin_?.accountById?.roles
                                      ?.rows
                                  )
                                );

                                currentProject._account_project_roles =
                                  getTeamAccountProjectRoles(
                                    currentProject,
                                    projectRolesUpdate
                                  );

                                currentProject.project.project_product_settings.sort(
                                  productsSortFunction
                                );

                                calculatedDetails.assignedProjects?.push(
                                  currentProject
                                );
                                calculatedDetails.assignedProjectIds.push(
                                  currentProject.project.id
                                );

                                // Add 1 row span per project
                                calculatedDetails.orgRowSpan =
                                  calculatedDetails.orgRowSpan + 1;

                                // Add row span for project products.
                                calculatedDetails.orgRowSpan =
                                  calculatedDetails.orgRowSpan +
                                  Math.max(
                                    currentProject.project
                                      .project_product_settings.length - 1,
                                    0
                                  );

                                return calculatedDetails;
                              },
                              {
                                assignedProjects: [],
                                orgRowSpan: 0,
                                assignedProjectIds: [],
                              }
                            );

                          const availableProjects =
                            org.projects?.rows?.filter(
                              (project) =>
                                !assignedProjectIds.includes(project.id)
                            ) || [];

                          const orgCanManageAccess =
                            canManageAccessByOrgId[org.id!] ?? false;
                          const showAddProjectButton = orgCanManageAccess;
                          const finalOrgRowSpan =
                            (showAddProjectButton &&
                              (availableProjects.length > 0 ||
                                assignedProjects?.length === 0)) ||
                            (!showAddProjectButton &&
                              assignedProjects?.length === 0)
                              ? orgRowSpan + 1
                              : orgRowSpan;
                          const isCollapsed = ctx.teamDetails[event.teamId!]
                            ?.state?.orgs[currentOrgIndex]
                            ? !!ctx.teamDetails[event.teamId!]?.state?.orgs[
                                currentOrgIndex
                              ]?.isCollapsed
                            : allOrgs.length > 3;

                          return {
                            showAddProjectButton,
                            canManageAccess: orgCanManageAccess,
                            name: org.name,
                            id: org.id,
                            parentAccount: org.parentAccount,
                            accountRoles,
                            availableAccountRoles,
                            assignedProjects: orderBy(
                              assignedProjects,
                              (assignedProject) =>
                                assignedProject.project.name.toLowerCase(),
                              'asc'
                            ),
                            orgRowSpan: finalOrgRowSpan,
                            availableProjects,
                            isPrimaryAccount: teamPrimaryAccountId === org.id,
                            roles: accountDetails?.admin_?.accountById?.roles,
                            isCollapsed,
                          };
                        }
                      );

                      return {
                        teamDetails: teamDetails.tenancy_teams_by_pk,
                        availableUsers,
                        orgs: teamOrgs,
                        canManageAccess:
                          !!accountDetails?.admin_?.organizationMetadata
                            ?.canManageAccess,
                        teamDetailUpdates: {
                          ...teamDetailUpdateInitialState,
                          projectRolesUpdate,
                          roleIdsToRemove,
                        },
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      // Update context only if incoming data has differences.
                      if (!data) return;
                      const teamState = {
                        ...data.teamDetails,
                        availableUsers: data.availableUsers,
                        orgs: data.orgs,
                        canManageAccess: data.canManageAccess,
                      };

                      if (!ctx.teamDetails[data.teamDetails.id]) {
                        ctx.teamDetails[data.teamDetails.id] = {
                          state: teamState,
                          teamDetailUpdates: data.teamDetailUpdates,
                          initialState: teamState,
                        };
                      } else if (
                        !isEqual(
                          ctx.teamDetails[data.teamDetails.id].state,
                          teamState
                        )
                      ) {
                        ctx.teamDetails[data.teamDetails.id].state = teamState;
                      }

                      return ctx;
                    },
                    onDoneTarget: 'idle',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading team: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                initiateNewTeam: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<{
                      availableUsers: NonNullable<
                        GetNewTeamDetailsQuery['tenancy_users']
                      >;
                      orgs: ShortOrgType[];
                      canManageAccess: boolean;
                    } | void> => {
                      if (
                        event.type !== 'manager.teamDetails.initiateNewTeam' ||
                        !event.orgId
                      ) {
                        return;
                      }

                      const [
                        { data: newTeamDetails, error: newTeamDetailsError },
                        { data: accountDetails, error: accountDetailsError },
                      ] = await Promise.all([
                        urqlGql.getNewTeamDetails({
                          accountId: event.orgId,
                        }),
                        urqlGql.getAccountDetails({ id: event.orgId }),
                      ]);

                      if (newTeamDetailsError) {
                        throw new Error(newTeamDetailsError.message);
                      }

                      if (accountDetailsError) {
                        throw new Error(accountDetailsError.message);
                      }

                      if (
                        !newTeamDetails?.tenancy_users ||
                        !accountDetails?.admin_?.accountById
                      ) {
                        return;
                      }

                      const availableUsers = sortBy(
                        newTeamDetails.tenancy_users,
                        'firstName',
                        'lastName'
                      );

                      const allOrgs = handleOrgsDataProcessing(accountDetails);

                      const canManageAccess =
                        !!accountDetails?.admin_?.organizationMetadata
                          ?.canManageAccess;

                      const canManageAccessByOrgId =
                        await fetchCanManageAccessByOrgId(
                          allOrgs,
                          fetchOrganizationMetadata,
                          { [event.orgId!]: canManageAccess }
                        );

                      return {
                        availableUsers,
                        orgs: allOrgs.map((org) => {
                          const orgCanManageAccess =
                            canManageAccessByOrgId[org.id!] ?? false;
                          return {
                            showAddProjectButton: orgCanManageAccess,
                            canManageAccess: orgCanManageAccess,
                            name: org.name,
                            id: org.id,
                            parentAccount: org.parentAccount,
                            accountRoles: [],
                            availableAccountRoles: processAccountRoles(
                              [],
                              event.loggedInUserHighestRole,
                              accountDetails?.admin_?.accountById?.roles
                            ).availableAccountRoles as AccountRole[],
                            assignedProjects: [],
                            availableProjects: org.projects?.rows,
                            isPrimaryAccount: event.orgId === org.id,
                            roles: accountDetails?.admin_?.accountById?.roles,
                            isCollapsed: allOrgs.length > 3,
                            orgRowSpan: 1,
                          };
                        }),
                        canManageAccess,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      // Update context only if incoming data has differences.
                      if (!data) return;

                      const teamState = {
                        ...teamDetailsInitialState,
                        availableUsers: data.availableUsers,
                        orgs: data.orgs,
                        canManageAccess: data.canManageAccess,
                      };

                      ctx.teamDetails[NEW_TEAM_TEMP_ID] = {
                        state: teamState,
                        teamDetailUpdates: teamDetailUpdateInitialState,
                        initialState: teamState,
                      };

                      return ctx;
                    },
                    onDoneTarget: 'idle',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error loading team: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                createNewTeam: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | {
                          team: NonNullable<
                            NonNullable<
                              NonNullable<CreateTeamMutation>['admin_']
                            >['createTeam']
                          >;
                          teamInfo: TeamInfoDetailsFormValues;
                        }
                      | undefined
                    > => {
                      if (event.type !== 'manager.teamDetails.createNewTeam')
                        return;
                      const { orgId, teamInfo } = event;
                      const teamDetails = ctx.teamDetails[NEW_TEAM_TEMP_ID];
                      if (!orgId || !teamDetails) return;

                      const stateUpdates = omit(
                        teamDetails.teamDetailUpdates,
                        'hasPendingUpdates',
                        'projectRolesUpdate'
                      );

                      stateUpdates.projectIdsToAdd =
                        stateUpdates.projectIdsToAdd.filter((projectId) =>
                          teamDetails.teamDetailUpdates.projectRolesUpdate.find(
                            (projectRoleUpdate) =>
                              projectRoleUpdate.id === projectId
                          )
                        );

                      const { data: createTeamData, error: createTeamError } =
                        await urqlGql.createTeam({
                          accountId: orgId,
                          ...teamInfo,
                          ...stateUpdates,
                        });

                      if (createTeamError) {
                        throw new Error(createTeamError.message);
                      }

                      if (!createTeamData?.admin_?.createTeam) {
                        throw new Error('Failed to create team');
                      }

                      const {
                        data: projectRolesData,
                        error: projectRolesError,
                      } = await urqlGql.setTeamAccountProjectRoles({
                        teamId: createTeamData.admin_.createTeam.id!,
                        projectRolesUpdate:
                          teamDetails.teamDetailUpdates.projectRolesUpdate,
                      });

                      if (projectRolesError) {
                        throw new Error(projectRolesError.message);
                      }

                      if (
                        !projectRolesData?.admin_?.setTeamAccountProjectsRoles
                      ) {
                        throw new Error('Failed to set team project roles');
                      }

                      return {
                        team: createTeamData.admin_.createTeam,
                        teamInfo,
                      };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      if (!data || !data.team.id) return;

                      sendUserMessage({
                        type: 'success',
                        text: 'Team successfully created',
                        autoClose: 3000,
                      });

                      const teamState = {
                        ...ctx.teamDetails[NEW_TEAM_TEMP_ID].state,
                        ...data.teamInfo,
                      };

                      ctx.teamDetails[data.team.id] = {
                        state: teamState,
                        teamDetailUpdates: teamDetailUpdateInitialState,
                        initialState: teamState,
                      };

                      ctx.teams?.push(data.team);
                      ctx.teams = sortBy(ctx.teams, 'name');
                      ctx.selectedTeam = data.team.id;
                      return ctx;
                    },
                    onDoneTarget: 'teamCreated',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error adding new team: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                teamCreated: {
                  after: {
                    1000: {
                      // Once user is invited, set state to idle.
                      target: 'idle',
                      actions: immerAssign((ctx, event) => {
                        delete ctx.teamDetails[NEW_TEAM_TEMP_ID];
                        return ctx;
                      }),
                    },
                  },
                },
                saveUpdates: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | {
                          teamId: string;
                          teamInfo: TeamInfoDetailsFormValues;
                          team: NonNullable<
                            NonNullable<
                              NonNullable<UpdateTeamMutation>['admin_']
                            >['updateTeam']
                          >;
                        }
                      | undefined
                    > => {
                      if (event.type !== 'manager.teamDetails.saveUpdates')
                        return;
                      const { teamId, orgId, teamInfo } = event;
                      if (!teamId || !orgId) return;
                      const teamDetails = ctx.teamDetails[teamId];
                      if (!teamDetails) return;

                      const stateUpdates = omit(
                        teamDetails.teamDetailUpdates,
                        'hasPendingUpdates',
                        'projectRolesUpdate'
                      );

                      stateUpdates.projectIdsToAdd =
                        stateUpdates.projectIdsToAdd.filter((projectId) =>
                          teamDetails.teamDetailUpdates.projectRolesUpdate.find(
                            (projectRoleUpdate) =>
                              projectRoleUpdate.id === projectId
                          )
                        );

                      const {
                        data: projectRolesData,
                        error: projectRolesError,
                      } = await urqlGql.setTeamAccountProjectRoles({
                        teamId,
                        projectRolesUpdate:
                          teamDetails.teamDetailUpdates.projectRolesUpdate,
                      });

                      if (projectRolesError) {
                        throw new Error(projectRolesError.message);
                      }

                      if (
                        !projectRolesData?.admin_?.setTeamAccountProjectsRoles
                      ) {
                        throw new Error('Failed to update team project roles');
                      }

                      const { data: teamUpdateData, error: teamUpdateError } =
                        await urqlGql.updateTeam({
                          id: teamId,
                          ...teamInfo,
                          ...stateUpdates,
                        });

                      if (teamUpdateError) {
                        throw new Error(teamUpdateError.message);
                      }

                      if (!teamUpdateData?.admin_?.updateTeam) {
                        throw new Error('Failed to update team');
                      }

                      return {
                        teamInfo,
                        teamId,
                        team: teamUpdateData?.admin_?.updateTeam,
                      };
                    },
                    onDoneTarget: 'idle',
                    onDoneAssignContext({ ctx, data }) {
                      sendUserMessage({
                        type: 'success',
                        text: 'Team details updated successfully',
                        autoClose: 3000,
                      });
                      if (!data) return;
                      // Update initial state to save state as a starting point.
                      ctx.teamDetails[data.teamId].state = {
                        ...ctx.teamDetails[data.teamId].state,
                        ...data.teamInfo,
                      };
                      ctx.teamDetails[data.teamId].initialState =
                        ctx.teamDetails[data.teamId].state;
                      ctx.teamDetails[data.teamId].teamDetailUpdates =
                        teamDetailUpdateInitialState;

                      const teamIndex = ctx.teams?.findIndex(
                        (team) => team.id === data.team.id
                      );

                      if (teamIndex != undefined && teamIndex > -1) {
                        ctx.teams[teamIndex] = data.team;
                      }

                      return ctx;
                    },
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error updating team details: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                deleteTeam: {
                  invoke: createInvokablePromise({
                    src: async (
                      ctx,
                      event
                    ): Promise<
                      | {
                          deletedTeamId: string;
                        }
                      | undefined
                    > => {
                      if (event.type !== 'manager.teamDetails.deleteTeam') {
                        return;
                      }
                      if (!event.teamId) return;

                      const { data, error } = await urqlGql.deleteTeams({
                        teamIds: [event.teamId],
                      });

                      if (error) {
                        throw new Error(error.message);
                      }

                      if (!data?.admin_?.deleteTeams?.[0]?.succeeded) {
                        throw new Error('Failed to delete team');
                      }

                      return { deletedTeamId: event.teamId };
                    },
                    onDoneAssignContext({ ctx, data }) {
                      // Update context only if incoming data has differences.
                      if (!data) return;
                      sendUserMessage({
                        type: 'success',
                        text: 'Team successfully deleted',
                        autoClose: 3000,
                      });

                      ctx.teams = ctx.teams?.filter(
                        (team) => team.id !== data.deletedTeamId
                      );
                      // ctx.selectedTeam = undefined;
                      return ctx;
                    },
                    onDoneTarget: 'teamDeleted',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error deleting team: ${event.data.message}`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                teamDeleted: {
                  after: {
                    1000: {
                      target: 'idle',
                    },
                  },
                },
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
            importUsers: {
              id: 'importUsers',
              initial: 'idle',
              on: {
                'manager.users.importUsers': {
                  target: '.loading',
                },
              },
              states: {
                idle: {},
                loading: {
                  invoke: createInvokablePromise({
                    src: async (ctx, event): Promise<void> => {
                      if (ctx.selectedOrgId === undefined) {
                        throw Error('Missing organization to import users!');
                      }

                      if (event.type !== 'manager.users.importUsers') return;

                      const request = new XMLHttpRequest();
                      request.withCredentials = true;
                      request.open(
                        'POST',
                        `${import.meta.env.FE_ADMIN_URL}/rest/import-users/${ctx.selectedOrgId}/`
                      );
                      const formData = new FormData();
                      formData.append('file', event.file);
                      request.send(formData);

                      await new Promise((resolve, reject) => {
                        request.onload = () => {
                          const res = JSON.parse(request.response);

                          if (request.status >= 200 && request.status <= 400) {
                            resolve(res);
                          } else {
                            reject(res);
                          }
                        };
                      });
                    },
                    onDoneTarget: 'loaded',
                    onErrorTarget: 'error',
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'error',
                            text: `Error importing users`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                    onDoneActions: [
                      {
                        type: 'sendUserMessage',
                        exec(ctx, event) {
                          sendUserMessage({
                            type: 'success',
                            text: `Users successfully imported`,
                            autoClose: 3000,
                          });
                        },
                      },
                    ],
                  }),
                },
                loaded: {},
                error: {
                  tags: [ErrorTags.xstate],
                  exit: assign((ctx, _event) => {
                    return {
                      ...ctx,
                      errors: [],
                    };
                  }),
                },
              },
            },
          },
        },
      },
    },
    global: {
      type: 'parallel',
      states: {
        // none of these should be nested --> might make sense to have a localization machine
        appletTemplates: {
          id: 'appletTemplates',
          initial: 'idle',
          states: {
            idle: {
              always: {
                target: 'loading',
                cond: (ctx) =>
                  !!ctx.featureFlags?.['environments-and-web-applets'],
              },
            },
            loading: {
              invoke: createInvokablePromise({
                id: 'getAppletTemplate',
                src: async (
                  ctx,
                  event
                ): Promise<
                  NonNullable<GetAppletTemplatesQuery['admin_']>['applets']
                > => {
                  const { data, error } = await urqlGql.getAppletTemplates();

                  if (haveErrors(data)) {
                    throw new Error(data.errors?.[0]?.message);
                  }

                  if (error) {
                    throw new Error(error.message);
                  }

                  return data?.admin_?.applets;
                },
                onDoneTarget: 'loaded',
                onDoneAssignContext({ ctx, data }) {
                  ctx.appletTemplates = data || [];
                },
                onErrorTarget: 'error',
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onErrorActions: [
                  {
                    type: 'sendUserMessage',
                    exec(ctx, event) {
                      sendUserMessage({
                        type: 'error',
                        text: `Error fetching applet-templates: ${event.data.message}`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
              }),
            },
            loaded: {},
            error: {
              tags: [ErrorTags.xstate],
              exit: assign((ctx, _event) => {
                return {
                  ...ctx,
                  errors: [],
                };
              }),
            },
          },
        },
        languages: {
          id: 'languages',
          initial: 'loading',
          states: {
            loading: {
              invoke: createInvokablePromise({
                id: 'getLanguages',
                src: async (
                  ctx,
                  event
                ): Promise<{
                  languages: ManagerContext['languages'];
                  localizationSettingsConfig: ManagerContext['localizationSettingsConfig'];
                }> => {
                  const { data, error } = await urqlGql.getLanguages();

                  if (haveErrors(data)) {
                    throw new Error(data.errors?.[0]?.message);
                  }

                  if (error) {
                    throw new Error(error.message);
                  }

                  const languages = data?.admin_?.locales
                    ? sort(data?.admin_?.locales).asc('name')
                    : [];

                  if (languages.length) {
                    languages.unshift({
                      name: 'Select a language',
                      id: '',
                      locale: '',
                      __typename: 'Locale',
                      languageCode: '',
                    });
                  }

                  return {
                    languages,
                    localizationSettingsConfig:
                      data?.admin_?.localizationSettingsConfig,
                  };
                },
                onDoneTarget: 'loaded',
                onDoneAssignContext({ ctx, data }) {
                  ctx.languages = data.languages;
                  ctx.localizationSettingsConfig =
                    data.localizationSettingsConfig;
                },
                onErrorTarget: 'error',
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onErrorActions: [
                  {
                    type: 'sendUserMessage',
                    exec(ctx, event) {
                      sendUserMessage({
                        type: 'error',
                        text: `Error fetching languages: ${event.data.message}`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
              }),
            },
            loaded: {},
            error: {
              tags: [ErrorTags.xstate],
              exit: assign((ctx, _event) => {
                return {
                  ...ctx,
                  errors: [],
                };
              }),
            },
          },
        },
        // should only be 24 time zones
        timezones: {
          id: 'timezones',
          initial: 'loading',
          states: {
            loading: {
              invoke: createInvokablePromise({
                id: 'getTimeZones',
                src: async (
                  ctx,
                  event
                ): Promise<ManagerContext['timezones'] | []> => {
                  const { data, error } = await urqlGql.getTimeZones();

                  if (haveErrors(data)) {
                    throw new Error(data.errors?.[0]?.message);
                  }

                  if (error) {
                    throw new Error(error.message);
                  }

                  const timeZoneData = data?.admin_?.timeZones;
                  const timeZones = timeZoneData
                    ? sort(timeZoneData).asc('label')
                    : [];

                  if (timeZoneData?.length) {
                    timeZones.unshift({
                      label: 'Select a time zone',
                      identifier: '',
                      id: '',
                      __typename: 'TimeZone',
                    });
                  }

                  return transformTimezonesData<Timezone>(timeZones);
                },
                onDoneTarget: 'loaded',
                onDoneAssignContext({ ctx, data }) {
                  ctx.timezones = data;
                },
                onErrorTarget: 'error',
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onErrorActions: [
                  {
                    type: 'sendUserMessage',
                    exec(ctx, event) {
                      sendUserMessage({
                        type: 'error',
                        text: `Error fetching TimeZones: ${event.data.message}`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
              }),
            },
            loaded: {},
            error: {
              tags: [ErrorTags.xstate],
              exit: assign((ctx, _event) => {
                return {
                  ...ctx,
                  errors: [],
                };
              }),
            },
          },
        },

        // products isn't affected by current org so this shouldn't live here
        products: {
          initial: 'idle',
          states: {
            idle: {
              // entry: () => {
              // console.log('ready to load product info!');
              // },
              always: {
                target: 'loading',
                cond: (context, event) => {
                  return context.userInfo?.status === UserStatus.Active;
                },
              },
            },
            loading: {
              invoke: createInvokablePromise({
                src: async (
                  ctx,
                  event
                ): Promise<{
                  products: ManagerContext['products'];
                  allProducts: ManagerContext['allProducts'];
                }> => {
                  const { data, error } = await urqlGql.getProducts();

                  if (haveErrors(data)) {
                    throw new Error(data.errors?.[0]?.message);
                  }

                  if (error) {
                    throw new Error(error.message);
                  }

                  return {
                    allProducts: data?.admin_?.allProducts,
                    products: data?.admin_?.allToolProducts,
                  };
                },
                onDoneTarget: 'loaded',
                onDoneAssignContext({ ctx, data }) {
                  if (data !== undefined) {
                    ctx.products = data.products;
                    ctx.allProducts = data.allProducts;
                    ctx.tools = ctx.tools.map((tool) => ({
                      ...tool,
                      productId:
                        data.allProducts?.find((prd) => prd.name === tool.name)
                          ?.id || '',
                    }));
                  }
                },
                onErrorTarget: 'error',
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onErrorActions: [
                  {
                    type: 'sendUserMessage',
                    exec(ctx, event) {
                      sendUserMessage({
                        type: 'error',
                        text: `Error fetching user orgs: ${event.data.message}`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
              }),
            },
            error: {
              tags: [ErrorTags.xstate],
              exit: assign((ctx, _event) => {
                return {
                  ...ctx,
                  errors: [],
                };
              }),
            },
            loaded: {
              // entry: (ctx) => {
              // console.log('loaded products!', ctx.products);
              // },
              always: {
                target: 'loading',
                cond: (context, event) => {
                  // return context.selectedOrgId !== context.
                  return false;
                },
              },
            },
          },
        },
      },
    },
    superAdmin: {
      initial: 'idle',
      states: {
        idle: {},
        dataBaseSnapshot: {
          id: 'dataBaseSnapshot',
          initial: 'loading',
          states: {
            loading: {
              invoke: createInvokablePromise({
                src: async (_ctx, event): Promise<void> => {
                  if (event.type !== 'manager.createDbSnapshot') return;

                  const { data, error } = await urqlGql.makeDbSnapshot();

                  if (haveErrors(data)) {
                    throw new Error(data.errors?.[0]?.message);
                  }

                  if (error) {
                    throw new Error(error.message);
                  }
                },
                onDoneTarget: 'loaded',
                onErrorTarget: 'error',
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onDoneActions: [
                  {
                    type: 'sendUserMessage',
                    exec(_ctx, _event) {
                      sendUserMessage({
                        type: 'success',
                        text: `DB snapshot created successfully`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
                onErrorActions: [
                  {
                    type: 'sendUserMessage',
                    exec(_ctx, event) {
                      sendUserMessage({
                        type: 'error',
                        text: `Error making db snapshot: ${event.data.message}`,
                        autoClose: 3000,
                      });
                    },
                  },
                ],
              }),
            },
            loaded: {},
            error: {
              tags: [ErrorTags.xstate],
              exit: assign((ctx, _event) => {
                return {
                  ...ctx,
                  errors: [],
                };
              }),
            },
          },
        },
      },
    },
  },
});
